/**
 * CharacterSheetHUD.ts
 * Comprehensive Testbed Inspector & Interactive Control Center for:
 * - Tab 1 (🧬 종족 & 30p 초기스탯): 6 Terra Anima Races, Class Restriction Matrix, DAoC 30 Bonus Point Allocator
 * - Tab 2 (📊 스탯 & 전문스킬): 7 Stats, HP-Proportional Move Speed, DAoC Spec Points (L pts for Lv.L) & Down-ranking
 * - Tab 3 (🛠️ 자율스킬 19종): Combat(3), Practice(4), Life(8), Common(4) 0.0~100.0% training & stat gain
 * - Tab 4 (⚒️ 멀티스킬 제작 & 🔮 인챈터): 3-Component Crafting (Blade+Hilt+Scabbard) & Enchanter NPC (+10 Cap)
 * - Tab 5 (🗺️ 7대 존 월드맵): Instant Zone Teleport across all 7 Terra Anima Zones & Boss Spawner
 */

import { AccountSaveSystem } from '../../core/AccountSaveSystem';
import { ApiClient } from '../../core/ApiClient';
import { Settings } from '../../core/Settings';
import { HudLayout, tileColor } from './DaocHud';
import { getZoneData, ZoneData } from '../world/ZoneMapData';
import {
  EntityState,
  RaceId,
  StatBlock,
  SelfSkillBlock,
  CraftedEquipment,
  HairStyleId,
  FacialFeatureId,
  INVENTORY_CAPACITY,
  INVENTORY_BASE_UNLOCKED,
  INVENTORY_UNLOCK_STEP,
  INVENTORY_UNLOCK_DIAMOND_COST,
  PersonalityAxes,
  PersonalityProfile,
} from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { EventBus } from '../../core/EventBus';
import {
  PERSONALITY_QUESTIONS,
  PersonalityQuestion,
  computePersonalityProfile,
  applyPersonalityDrift,
  resolveAlignment,
  resolveMbti,
  resolveSoulTitle,
  PersonalityActionType,
} from '../character/PersonalityQuiz';
import {
  TERRA_ANIMA_RACES,
  SELF_SKILL_METAS,
  CLASS_SPEC_MULTIPLIERS,
  MULTI_SKILL_RECIPES,
  SKIN_COLOR_PALETTE,
  HAIR_COLOR_PALETTE,
  ARMOR_COLOR_PALETTE,
  HAIR_STYLE_OPTIONS,
  FACIAL_FEATURE_OPTIONS,
  TITLE_PREFIX_OPTIONS,
  HORSE_COAT_PALETTE,
  getDefaultAppearanceForRole,
  randomizeCharacterAppearance,
  getBonusStatPointCost,
  getTotalBonusPointsUsed,
  calcCumulativeSkillCost,
  recalculateEntityStats,
  getEffectiveSkillLevel,
  getEffectiveMoveSpeed,
  getDerivedCombatMetrics,
  practiceSelfSkill,
  craftMultiSkillEquipment,
  enchantEquipmentByNpc,
  initEntityInventory20,
  addItemToInventory,
  transferInventorySlot,
  sortInventory20,
  getInventoryTotalWeight,
  getFameRankInfo,
  adjustEntityFame,
  buyItemFromNpcInventory,
  stealFromInventorySlot,
} from '../character/CharacterSystem';
import { CLASS_SKILL_DEFINITIONS, getScaledSkillMetrics } from '../combat/CombatTypes';
import { SkillEngine } from '../combat/SkillEngine';
import { SpriteRenderer } from './SpriteRenderer';
import { STABLE_NODES, StableRouteSystem } from '../world/StableRouteSystem';
import { TILE_PROPERTY_SPECS } from '../world/TilePropertySystem';
import { Currency } from '../../core/Currency';
import { moneySelectorModalInstance } from '../ui/MoneySelectorModal';

export type InspectorTab =
  | 'race_stat'
  | 'personality'
  | 'inventory_20'
  | 'spec_skill'
  | 'self_skill'
  | 'craft_enchant'
  | 'world_map';

export interface SheetWindowCfg {
  key: string;
  title: string;
  icon: string;
  tabs: InspectorTab[];
  w: number;
  h: number;
  dx: number;
  dy: number;
}

const WORLD_MAP_ZONES: Array<{ id: number; name: string }> = [
  { id: 1, name: '솔라리아 구릉지' },
  { id: 2, name: '그란델 평원' },
  { id: 3, name: '실바나 대삼림' },
  { id: 4, name: '보레아스 설원' },
  { id: 5, name: '네뷸라 늪지' },
  { id: 6, name: '이그니스 황무지' },
  { id: 7, name: '루미나 성역섬' },
  { id: 8, name: '솔라리스 성' },
  { id: 9, name: '하우징 구역' },
  { id: 10, name: '고대 공동묘지 B1' },
  { id: 11, name: '고대 공동묘지 B2' },
  { id: 12, name: '고대 공동묘지 B3' },
];
const worldMapZoneCache = new Map<number, ZoneData>();

export class CharacterSheetHUD {
  private cfg?: SheetWindowCfg;
  private mapZoneSel: number | null = null;
  private mapTimer: number | null = null;
  private mapBaseCache = new Map<number, HTMLCanvasElement>();
  private modalEl: HTMLElement | null = null;
  private isOpen = false;
  private activeTab: InspectorTab = 'race_stat';
  private registry: EntityRegistry;
  private onTeleportZone?: (zoneId: number) => void;
  private currentZoneId = 1;
  private latestCraftedItem: CraftedEquipment | null = null;
  private spritePreviewRenderer = new SpriteRenderer();
  private previewAnimFrameId: number | null = null;
  private selectedInvCharId: string | null = null;
  private selectedInvNpcId: string = 'npc_stable_jackson';
  private selectedFromStableId: string = 'stable_z1_crossroads';
  private selectedToStableId: string = 'stable_z1_oakhaven';
  private stableRouteSystem?: StableRouteSystem;
  private selectedPersonalityCharId: string | null = null;
  private isPersonalityQuizMode: boolean = false;
  private quizStep: number = 0; // 0..7
  private quizSelectedAnswers: number[] = [0, 0, 0, 0, 0, 0, 0, 0];

  constructor(
    registry: EntityRegistry,
    onTeleportZone?: (zoneId: number) => void,
    stableRouteSystem?: StableRouteSystem,
    cfg?: SheetWindowCfg
  ) {
    this.cfg = cfg;
    if (cfg) this.activeTab = cfg.tabs[0];
    this.registry = registry;
    this.onTeleportZone = onTeleportZone;
    this.stableRouteSystem = stableRouteSystem;
  }

  public openNpcInventory(npcId: string): void {
    this.selectedInvNpcId = npcId;
    this.openTab('inventory_20');
  }

  public setCurrentZoneId(zoneId: number): void {
    this.currentZoneId = zoneId;
    if (this.isOpen) this.render();
  }

  public mount(parent: HTMLElement): void {
    if (this.cfg) {
      // DAoC 스타일 개별 창 모드: 드래그 이동 / 크기 조절 가능한 독립 창
      const w = document.createElement('div');
      w.id = `char-window-${this.cfg.key}`;
      w.style.display = 'none';
      w.style.flexDirection = 'column';
      w.style.overflow = 'hidden';
      w.style.color = '#e8dcc0';
      w.style.userSelect = 'none';
      parent.appendChild(w);
      this.modalEl = w;
      HudLayout.wrap(w, {
        id: `win-${this.cfg.key}`,
        mode: 'free',
        anchor: 'tl',
        dx: this.cfg.dx,
        dy: this.cfg.dy,
        w: this.cfg.w,
        h: this.cfg.h,
        minW: 300,
        minH: 160,
      });
      return;
    }

    const el = document.createElement('div');
    el.id = 'character-sheet-modal';
    el.style.position = 'absolute';
    el.style.top = '52px';
    el.style.right = '16px';
    el.style.width = '720px';
    el.style.maxHeight = 'calc(100vh - 140px)';
    el.style.backgroundColor = 'rgba(13, 17, 23, 0.96)';
    el.style.border = '1.5px solid #38bdf8';
    el.style.borderRadius = '12px';
    el.style.boxShadow = '0 16px 48px rgba(0,0,0,0.85)';
    el.style.backdropFilter = 'blur(10px)';
    el.style.color = '#f1f5f9';
    el.style.zIndex = '90';
    el.style.display = 'none';
    el.style.flexDirection = 'column';
    el.style.overflow = 'hidden';
    el.style.userSelect = 'none';

    parent.appendChild(el);
    this.modalEl = el;

    window.addEventListener('keydown', (e) => {
      if ((window as any).__TERRA_GAME_STATE__ && (window as any).__TERRA_GAME_STATE__() !== 'IN_WORLD') return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const action = Settings.actionForKey(e.key);
      if (action === 'character') {
        this.toggle('spec_skill');
      } else if (action === 'inventory') {
        this.toggle('inventory_20');
      } else if (action === 'craft') {
        this.toggle('craft_enchant');
      } else if (action === 'map') {
        this.toggle('world_map');
      }
    });
  }

  public toggle(tab?: InspectorTab): void {
    if (tab && (!this.isOpen || this.activeTab !== tab)) {
      this.activeTab = tab;
      this.isOpen = true;
    } else {
      this.isOpen = !this.isOpen;
    }
    if (this.modalEl) {
      this.modalEl.style.display = this.isOpen ? 'flex' : 'none';
    }
    if (this.isOpen) {
      this.render();
      if (this.cfg) HudLayout.focus(`win-${this.cfg.key}`);
    }
  }

  public openTab(tab: InspectorTab): void {
    this.activeTab = tab;
    this.isOpen = true;
    if (this.modalEl) {
      this.modalEl.style.display = 'flex';
    }
    this.render();
    if (this.cfg) HudLayout.focus(`win-${this.cfg.key}`);
  }

  public refreshIfOpen(): void {
    if (this.isOpen) {
      this.render();
    }
  }

  public render(): void {
    if (!this.modalEl || !this.isOpen) return;
    const player = this.registry.getPlayer();
    if (!player) return;

    recalculateEntityStats(player, false);

    const tabs: Array<{ id: InspectorTab; label: string; badge: string }> = [
      { id: 'race_stat', label: '🎨 커스텀·30p', badge: ' 생성 ' },
      { id: 'personality', label: '🌟 12세 성인식·영혼성향', badge: ' [P] ' },
      { id: 'inventory_20', label: '🎒 50칸 인벤토리', badge: ' [I] ' },
      { id: 'spec_skill', label: '📊 7스탯 & 전문스킬', badge: ' [C] ' },
      { id: 'self_skill', label: '🛠️ 자율스킬 19종', badge: ' 0~100% ' },
      { id: 'craft_enchant', label: '⚒️ 제작 & 인챈터', badge: ' [K] ' },
      { id: 'world_map', label: '🗺️ 7대 존 월드맵', badge: ' [M] ' },
    ];

    const fameVal = player.fame ?? 180;
    const fameRank = getFameRankInfo(fameVal);

    let tabBarHtml = `
      <div style="display:flex; align-items:center; justify-content:space-between; background:#0f172a; padding:10px 14px; border-bottom:1px solid #1e293b;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="font-size:18px;">${player.avatarIcon}</span>
          <div>
            <div style="font-size:14px; font-weight:bold; color:#f8fafc; display:flex; align-items:center; gap:8px;">
              <span>👑 파티리더: ${player.name}</span>
              <span style="color:#f59e0b;">Lv.${player.level || 15} ${player.job}</span>
              <span style="background:rgba(15,23,42,0.9); border:1px solid ${fameRank.color}; color:${fameRank.color}; padding:1px 7px; border-radius:4px; font-size:11px;">
                ${fameRank.badge} 명성: ${fameVal} pt (${fameRank.rankTitle.split(' (')[0]})
              </span>
              <span style="background:rgba(120,53,15,0.4); border:1px solid #fde047; color:#fde047; padding:1px 7px; border-radius:4px; font-size:11px;">
                🪙 소지금: ${Currency.format(player.gold ?? Currency.toCopper({ gold: 100 }))}
              </span>
            </div>
            <div style="font-size:11px; color:#94a3b8;">
              출신 종족: <b style="color:#38bdf8;">${TERRA_ANIMA_RACES[player.race || 'solarian'].nameKr}</b> | 포인트 배율: <b style="color:#4ade80;">${CLASS_SPEC_MULTIPLIERS[player.role]}x</b> | 도둑질 전과: <b style="color:#f87171;">${player.theftCount ?? 0}회</b>
            </div>
          </div>
        </div>
        <button id="btn-close-inspector" style="background:#334155; border:1px solid #475569; color:#f8fafc; padding:4px 10px; border-radius:6px; cursor:pointer; font-size:11px; font-weight:bold;">✕ 닫기</button>
      </div>
      <div style="display:flex; background:#1e293b; border-bottom:1px solid #334155; padding:4px 8px; gap:4px;">
    `;

    for (const t of tabs) {
      const isAct = this.activeTab === t.id;
      tabBarHtml += `
        <button data-tab-id="${t.id}" style="
          flex:1;
          background:${isAct ? 'linear-gradient(135deg, #0284c7, #2563eb)' : 'transparent'};
          border:1px solid ${isAct ? '#38bdf8' : 'transparent'};
          color:${isAct ? '#ffffff' : '#94a3b8'};
          padding:6px 4px;
          border-radius:6px;
          cursor:pointer;
          font-size:11px;
          font-weight:bold;
        ">${t.label}</button>
      `;
    }
    tabBarHtml += `</div>`;

    let bodyHtml = '';
    if (this.activeTab === 'race_stat') {
      bodyHtml = this.renderRaceAndBonusTab(player);
    } else if (this.activeTab === 'personality') {
      bodyHtml = this.renderPersonalityTab(player);
    } else if (this.activeTab === 'inventory_20') {
      bodyHtml = this.renderInventory20Tab(player);
    } else if (this.activeTab === 'spec_skill') {
      bodyHtml = this.renderStatsAndSpecTab(player);
    } else if (this.activeTab === 'self_skill') {
      bodyHtml = this.renderSelfSkillsTab(player);
    } else if (this.activeTab === 'craft_enchant') {
      bodyHtml = this.renderCraftAndEnchanterTab(player);
    } else if (this.activeTab === 'world_map') {
      bodyHtml = this.renderWorldMapTab(player);
    }

    if (this.cfg) {
      const cfg = this.cfg;
      const visibleTabs = cfg.tabs.map((id) => tabs.find((t) => t.id === id)!).filter(Boolean);
      const titleBar = `
        <div class="daoc-title" style="flex-shrink:0;align-items:center;">
          <span>${cfg.icon} ${cfg.title} <span style="font-weight:normal;color:#cdb98a;">· ${player.name} Lv.${player.level || 1}</span></span>
          <span style="display:flex;align-items:center;gap:10px;">
            <span style="font-weight:normal;color:#fde047;">🪙 ${Currency.format(player.gold ?? 0)} · 명성 ${fameVal}</span>
            <button id="btn-close-inspector" style="background:none;border:none;color:#f3d98b;cursor:pointer;font-size:14px;line-height:1;padding:0 2px;">✕</button>
          </span>
        </div>`;
      const tabRow =
        visibleTabs.length > 1
          ? `<div style="display:flex;gap:3px;padding:3px 6px;background:#241b10;border-bottom:1px solid #000;flex-shrink:0;">${visibleTabs
              .map((t) => {
                const on = t.id === this.activeTab;
                return `<button data-tab-id="${t.id}" style="flex:1;background:${on ? '#5a431f' : '#0c0905'};border:1px solid ${on ? '#f3d98b' : '#3f2f17'};color:${on ? '#fff' : '#cdb98a'};padding:4px 2px;border-radius:3px;cursor:pointer;font-size:11px;font-weight:bold;">${t.label}</button>`;
              })
              .join('')}</div>`
          : '';
      this.modalEl.innerHTML = `${titleBar}${tabRow}<div style="padding:12px;overflow-y:auto;flex:1;min-height:0;font-size:12px;">${bodyHtml}</div>`;
      this.bindEvents(player);
      return;
    }

    this.modalEl.innerHTML = `
      ${tabBarHtml}
      <div style="padding:14px; overflow-y:auto; max-height:calc(100vh - 230px); font-size:12px;">
        ${bodyHtml}
      </div>
    `;

    this.bindEvents(player);
  }

  // ==========================================================================
  // TAB 2: 🎒 50칸 인벤토리 (기본 20칸 + 5칸 단위 다이아 언락 시스템)
  // ==========================================================================
  private renderInventory20Tab(player: EntityState): string {
    const leader = player;
    initEntityInventory20(leader);

    const unlockedCount = Math.min(INVENTORY_CAPACITY, Math.max(INVENTORY_BASE_UNLOCKED, leader.unlockedSlots ?? INVENTORY_BASE_UNLOCKED));
    const leaderMetrics = getDerivedCombatMetrics(leader);
    const leaderWeight = getInventoryTotalWeight(leader);
    const leaderUsedSlots = (leader.inventory || []).filter((it, idx) => it !== null && idx < unlockedCount).length;
    const canExpand = unlockedCount < INVENTORY_CAPACITY;

    const rarityColors: Record<string, { border: string; bg: string; badge: string }> = {
      common: { border: '#475569', bg: 'rgba(30, 41, 59, 0.85)', badge: '#94a3b8' },
      magic: { border: '#3b82f6', bg: 'rgba(29, 78, 216, 0.45)', badge: '#60a5fa' },
      fine: { border: '#3b82f6', bg: 'rgba(29, 78, 216, 0.45)', badge: '#60a5fa' },
      rare: { border: '#eab308', bg: 'rgba(161, 98, 7, 0.45)', badge: '#fde047' },
      masterwork: { border: '#eab308', bg: 'rgba(161, 98, 7, 0.45)', badge: '#fde047' },
      heroic: { border: '#a855f7', bg: 'rgba(126, 34, 206, 0.45)', badge: '#c084fc' },
      legendary: { border: '#f59e0b', bg: 'rgba(180, 83, 9, 0.55)', badge: '#fbbf24' },
    };

    // 1) Render Player's 50-Slot Inventory (10 Columns x 5 Rows)
    const renderLeader50SlotsGrid = (owner: EntityState): string => {
      initEntityInventory20(owner);
      const slots = owner.inventory!;
      let gridHtml = `<div style="display:grid; grid-template-columns:repeat(10, 1fr); gap:6px; max-height:480px; overflow-y:auto; padding-right:4px;">`;

      for (let i = 0; i < INVENTORY_CAPACITY; i++) {
        const item = slots[i];
        const slotNum = String(i + 1).padStart(2, '0');
        const isLocked = i >= unlockedCount;

        if (isLocked) {
          // Locked Slot (5칸 단위 언락 대기)
          const isNextBatch = i === unlockedCount;
          gridHtml += `
            <div data-locked-slot="${i}" style="
              height:72px;
              background:rgba(15, 10, 25, 0.7);
              border:1px dashed #6b21a8;
              border-radius:6px;
              display:flex;
              flex-direction:column;
              align-items:center;
              justify-content:center;
              position:relative;
              color:#a855f7;
              cursor:${isNextBatch ? 'pointer' : 'not-allowed'};
              transition:all 0.15s;
            " title="${isNextBatch ? `💎 50다이아로 ${unlockedCount + 1}~${unlockedCount + 5}번 슬롯(5칸)을 즉시 확장합니다!` : '잠긴 슬롯'}">
              <span style="position:absolute; top:2px; left:4px; font-size:7.5px; font-family:monospace; color:#7e22ce;">#${slotNum}</span>
              <span style="font-size:16px;">🔒</span>
              ${isNextBatch ? `<span style="font-size:7.5px; font-weight:bold; color:#fde047; background:#581c87; padding:1px 3px; border-radius:3px; margin-top:2px;">+5칸 확장</span>` : `<span style="font-size:7.5px; color:#6b21a8;">잠김</span>`}
            </div>
          `;
        } else if (!item) {
          // Empty Unlocked Slot
          gridHtml += `
            <div style="
              height:72px;
              background:rgba(15, 23, 42, 0.65);
              border:1px dashed #334155;
              border-radius:6px;
              display:flex;
              flex-direction:column;
              align-items:center;
              justify-content:center;
              position:relative;
              color:#475569;
            ">
              <span style="position:absolute; top:2px; left:4px; font-size:7.5px; font-family:monospace;">#${slotNum}</span>
              <span style="font-size:8.5px;">빈 슬롯</span>
            </div>
          `;
        } else {
          // Filled Unlocked Slot
          const rStyle = rarityColors[item.rarity] || rarityColors.common;
          const isHorseTicket = Boolean(
            item.routeTargetStableId || item.name.includes('승마표') || item.name.includes('마패')
          );

          gridHtml += `
            <div title="[#${slotNum}] ${item.name}\n${item.description}\n무게: ${item.weightKg}kg | 단가: ${Currency.format(item.valueGold)}" style="
              height:72px;
              background:${rStyle.bg};
              border:1.5px solid ${item.isStolen ? '#ef4444' : rStyle.border};
              border-radius:6px;
              padding:2px 3px;
              display:flex;
              flex-direction:column;
              align-items:center;
              justify-content:space-between;
              position:relative;
            ">
              <div style="width:100%; display:flex; justify-content:space-between; align-items:center;">
                <span style="font-size:7.5px; color:${item.isStolen ? '#fca5a5' : '#94a3b8'}; font-family:monospace;">${item.isStolen ? '🕵️' : `#${slotNum}`}</span>
                <span style="font-size:8px; font-weight:bold; color:#fde047; background:rgba(0,0,0,0.55); padding:0 3px; border-radius:3px;">x${item.quantity}</span>
              </div>
              <div style="font-size:16px; line-height:1;">${item.icon}</div>
              <div style="font-size:8px; color:#f8fafc; font-weight:bold; max-width:100%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; text-align:center;">
                ${item.name.replace(/\s*\(.*?\)/, '').replace(/\s*\[.*?\]/, '')}
              </div>
              <div style="width:100%; display:flex; gap:2px; justify-content:center;">
                ${
                  isHorseTicket
                    ? `<button data-hand-ticket-slot="${i}" title="말 상인에게 승마표를 전달하여 출발합니다!" style="width:100%; background:linear-gradient(135deg, #d97706, #b45309); border:1px solid #fde047; color:#fff; font-size:7.5px; padding:1px 2px; border-radius:3px; cursor:pointer; font-weight:bold;">🎫승마</button>`
                    : item.category === 'consumable' || item.craftedEquip
                    ? `<button data-char-slot-use="${i}" style="flex:1; background:#059669; border:1px solid #6ee7b7; color:#fff; font-size:7px; padding:1px 2px; border-radius:3px; cursor:pointer; font-weight:bold;">사용</button>`
                    : ''
                }
              </div>
            </div>
          `;
        }
      }
      gridHtml += `</div>`;
      return gridHtml;
    };

    return `
      <!-- 1. Header Guide Banner -->
      <div style="background:linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95)); border:1.5px solid #f59e0b; border-radius:8px; padding:10px 12px; margin-bottom:12px;">
        <div style="display:flex; align-items:center; justify-content:space-between; gap:10px;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-size:24px;">🎒</span>
            <div>
              <div style="font-weight:bold; color:#fde047; font-size:12.5px;">
                개인 인벤토리 (최대 50칸 · 기본 20칸 + 5칸 단위 💎다이아 언락)
              </div>
              <div style="font-size:10px; color:#cbd5e1; line-height:1.45;">
                ① <b>인벤토리 슬롯 확장:</b> 기본 20칸 제공, <b>5칸마다 💎${INVENTORY_UNLOCK_DIAMOND_COST} 보석(다이아)</b>으로 최대 50칸까지 확장 가능합니다.<br/>
                ② <b>사냥 시 골드 자동 균등 분배:</b> 몬스터 사냥 시 획득한 금액은 <b>파티원 전원에게 1/N 균등하게 즉시 지급</b>됩니다.<br/>
                ③ <b>사냥 전리품 아이템:</b> 사냥 중 드롭된 모든 아이템은 <b>파티 리더의 인벤토리로 자동 일괄 지급</b>됩니다!
              </div>
            </div>
          </div>
          <div style="display:flex; flex-direction:column; align-items:flex-end; gap:4px; min-width:125px;">
            <span style="font-size:9.5px; color:#94a3b8;">리더 말 털색:</span>
            <div style="display:flex; gap:3px;">
              ${HORSE_COAT_PALETTE.map((c) => {
                const isSel = (leader.horseColor || '#f8fafc').toLowerCase() === c.hex.toLowerCase();
                return `<button data-horse-coat="${c.hex}" title="${c.name}" style="
                  width:16px; height:16px; border-radius:50%; background:${c.hex};
                  border:${isSel ? '2px solid #fde047' : '1px solid #475569'};
                  cursor:pointer;
                "></button>`;
              }).join('')}
            </div>
          </div>
        </div>
      </div>

      <!-- 2. Player 50-Slot Inventory Panel -->
      <div style="background:#0f172a; border:1.5px solid #38bdf8; border-radius:8px; padding:12px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; flex-wrap:wrap; gap:8px;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="background:#1e293b; border:1px solid #38bdf8; color:#7dd3fc; font-size:11px; font-weight:bold; padding:2px 8px; border-radius:4px;">
              보유 슬롯: <b>${unlockedCount}</b> / ${INVENTORY_CAPACITY}칸 (${leaderUsedSlots}칸 사용 중)
            </span>
            ${
              canExpand
                ? `<button id="btn-expand-inv-5" style="background:linear-gradient(135deg, #7e22ce, #a855f7); border:1px solid #d8b4fe; color:#fff; padding:2px 10px; border-radius:4px; font-size:10.5px; font-weight:bold; cursor:pointer; display:flex; align-items:center; gap:4px;">
                    <span>💎 +5칸 확장 (50다이아)</span>
                  </button>`
                : `<span style="color:#4ade80; font-size:10px; font-weight:bold;">✔ 최대 50칸 완료</span>`
            }
          </div>
          <div style="display:flex; align-items:center; gap:6px;">
            <button id="btn-sort-char-inv" style="background:#1e293b; border:1px solid #475569; color:#cbd5e1; padding:3px 9px; border-radius:4px; cursor:pointer; font-size:11px; font-weight:bold;">🧹 정렬</button>
            <div style="display:inline-flex; align-items:center; gap:4px; background:#111827; border:1px solid #334155; padding:2px 6px; border-radius:4px;">
              <span style="font-size:11px; color:#94a3b8; font-weight:bold;">👑 루팅필터:</span>
              <select id="select-loot-rarity" style="background:#1e293b; border:1px solid #475569; color:#f8fafc; font-size:11px; font-weight:bold; padding:2px 4px; border-radius:3px; outline:none; cursor:pointer;">
                <option value="common" style="color:#94a3b8; font-weight:bold;" ${(leader.lootFilterRarity || 'common') === 'common' ? 'selected' : ''}>일반(회색)</option>
                <option value="magic" style="color:#60a5fa; font-weight:bold;" ${leader.lootFilterRarity === 'magic' ? 'selected' : ''}>마법(파란색)</option>
                <option value="rare" style="color:#fde047; font-weight:bold;" ${leader.lootFilterRarity === 'rare' ? 'selected' : ''}>희귀(노란색)</option>
                <option value="heroic" style="color:#c084fc; font-weight:bold;" ${leader.lootFilterRarity === 'heroic' ? 'selected' : ''}>영웅(보라색)</option>
                <option value="legendary" style="color:#fbbf24; font-weight:bold;" ${leader.lootFilterRarity === 'legendary' ? 'selected' : ''}>전설(황금색)</option>
              </select>
              <button id="btn-apply-loot-filter" style="background:linear-gradient(135deg, #0284c7, #0ea5e9); border:1px solid #38bdf8; color:#fff; padding:2px 8px; border-radius:3px; cursor:pointer; font-size:11px; font-weight:bold;" title="선택한 등급 이상 아이템만 리더 인벤토리에 수납하고 미만은 파티원에게 자동 분배합니다.">필터적용</button>
            </div>
          </div>
        </div>

        <div style="display:flex; justify-content:space-between; align-items:center; font-size:11px; color:#cbd5e1; background:#1e293b; padding:6px 10px; border-radius:5px; margin-bottom:10px;">
          <span>무게: <b style="color:#4ade80;">${leaderWeight} / ${leaderMetrics.maxWeightKg} kg</b></span>
          
          <!-- Clickable 4-tier currency badges: (코퍼아이콘)123C (실버아이콘)456S (골드아이콘)789G (플래티넘아이콘)10P (0원이라도 0C 0S 0G 0P 상시 표시) -->
          <div style="display:flex; align-items:center; gap:5px;">
            <span style="color:#94a3b8; font-size:10px;">소지금(클릭 시 거래):</span>
            <button data-money-click="copper" title="클릭하여 거래할 코퍼(C) 수량 지정" style="background:#2d170b; border:1px solid #f97316; color:#f97316; padding:2px 6px; border-radius:3px; font-weight:bold; font-size:10px; cursor:pointer;">
              🪙${Currency.breakdown(leader.gold ?? 0).copper.toLocaleString()}C
            </button>
            <button data-money-click="silver" title="클릭하여 거래할 실버(S) 수량 지정" style="background:#1e293b; border:1px solid #cbd5e1; color:#cbd5e1; padding:2px 6px; border-radius:3px; font-weight:bold; font-size:10px; cursor:pointer;">
              🪙${Currency.breakdown(leader.gold ?? 0).silver.toLocaleString()}S
            </button>
            <button data-money-click="gold" title="클릭하여 거래할 골드(G) 수량 지정" style="background:#3b2d07; border:1px solid #fde047; color:#fde047; padding:2px 6px; border-radius:3px; font-weight:bold; font-size:10px; cursor:pointer;">
              🪙${Currency.breakdown(leader.gold ?? 0).gold.toLocaleString()}G
            </button>
            <button data-money-click="platinum" title="클릭하여 거래할 플래티넘(P) 수량 지정" style="background:#281b3d; border:1px solid #a78bfa; color:#a78bfa; padding:2px 6px; border-radius:3px; font-weight:bold; font-size:10px; cursor:pointer;">
              🪙${Currency.breakdown(leader.gold ?? 0).platinum.toLocaleString()}P
            </button>
          </div>
        </div>

        ${renderLeader50SlotsGrid(leader)}
      </div>
    `;
  }

  // ==========================================================================
  // TAB 1: 🎨 캐릭터 이름·외형 커스터마이징 & 🧬 6대 종족 & 🎲 30p 초기 스탯 배분
  // ==========================================================================
  private renderRaceAndBonusTab(player: EntityState): string {
    if (!player.appearance) {
      player.appearance = getDefaultAppearanceForRole(player.role);
    }
    const app = player.appearance;
    const curRace = player.race || 'solarian';
    const raceDef = TERRA_ANIMA_RACES[curRace];
    const bonus = player.allocatedBonusStats || { str: 10, con: 10, dex: 0, qui: 0, int: 0, pie: 10, cha: 0 };
    const usedPts = getTotalBonusPointsUsed(bonus);
    const remPts = 30 - usedPts;

    // Build Swatch Helpers
    const renderColorSwatches = (
      palette: Array<{ name: string; hex: string }>,
      currentHex: string,
      dataAttr: string
    ) => {
      return palette
        .map((c) => {
          const isSel = c.hex.toLowerCase() === currentHex.toLowerCase();
          return `<button ${dataAttr}="${c.hex}" title="${c.name}" style="
            width:19px; height:19px; border-radius:50%; background:${c.hex};
            border:${isSel ? '2.5px solid #fde047' : '1.5px solid #475569'};
            box-shadow:${isSel ? '0 0 6px #fde047' : 'none'};
            cursor:pointer; padding:0;
          "></button>`;
        })
        .join('');
    };

    let html = `
      <!-- 1. Character Name & Appearance Customization Studio -->
      <div style="margin-bottom:12px; background:linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,41,59,0.85)); border:1.5px solid #f59e0b; border-radius:8px; padding:10px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div style="font-weight:bold; color:#fde047; font-size:13px;">
            🎨 1. 캐릭터 생성: 이름 · 칭호 · 외형 커스터마이징 (실시간 캔버스 반영)
          </div>
          <div style="display:flex; gap:5px;">
            <button id="btn-custom-random" style="background:#7e22ce; border:1px solid #d8b4fe; color:#fff; padding:3px 8px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">🎲 랜덤 외형</button>
            <button id="btn-custom-reset" style="background:#334155; border:1px solid #64748b; color:#e2e8f0; padding:3px 8px; border-radius:4px; cursor:pointer; font-size:10px;">🔄 기본 외형</button>
          </div>
        </div>

        <div style="display:grid; grid-template-columns: 165px 1fr; gap:12px; align-items:stretch;">
          <!-- Left: 2.4x Zoomed Live Sprite Preview Canvas -->
          <div style="background:#090d16; border:1.5px solid #334155; border-radius:8px; padding:8px; display:flex; flex-direction:column; align-items:center; justify-content:space-between;">
            <div style="font-size:10px; color:#38bdf8; font-weight:bold; text-align:center;">
              &lt;${app.titlePrefix || '초보 모험가'}&gt;<br/>
              <span style="color:#f8fafc; font-size:11px;">${player.name}</span>
            </div>
            <canvas id="custom-preview-canvas" width="145" height="145" style="background:radial-gradient(circle, rgba(56,189,248,0.14) 0%, rgba(9,13,22,0) 70%); border-radius:6px;"></canvas>
            <div style="display:flex; flex-direction:column; gap:4px; width:100%;">
              <button id="btn-toggle-helm" style="background:${app.showHelm ? '#0284c7' : '#334155'}; border:1px solid #38bdf8; color:#fff; padding:4px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">
                🪖 투구/모자: ${app.showHelm ? '표시 ON' : '숨김 (헤어노출)'}
              </button>
              <button id="btn-preview-attack" style="background:#b45309; border:1px solid #fde047; color:#fff; padding:4px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">
                ⚔️ 무기 휘두르기 모션
              </button>
            </div>
          </div>

          <!-- Right: Name Input + Appearance Selectors -->
          <div style="display:flex; flex-direction:column; gap:7px; justify-content:space-between;">
            <!-- Row A: Character Name & Title Prefix -->
            <div style="display:grid; grid-template-columns: 1.2fr 1fr auto; gap:6px; background:#0f172a; padding:7px; border-radius:6px; border:1px solid #1e293b; align-items:center;">
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:2px;">✍️ 캐릭터 이름 (Name)</div>
                <input id="input-char-name" type="text" value="${player.name}" maxlength="20" style="width:100%; background:#1e293b; border:1px solid #38bdf8; color:#f8fafc; padding:4px 6px; border-radius:4px; font-size:11px; font-weight:bold; box-sizing:border-box;" />
              </div>
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:2px;">👑 캐릭터 칭호 (Title)</div>
                <select id="select-char-title" style="width:100%; background:#1e293b; border:1px solid #475569; color:#fde047; padding:4px; border-radius:4px; font-size:11px; font-weight:bold;">
                  ${TITLE_PREFIX_OPTIONS.map((t) => `<option value="${t}" ${app.titlePrefix === t ? 'selected' : ''}>&lt;${t}&gt;</option>`).join('')}
                </select>
              </div>
              <button id="btn-apply-name" style="margin-top:14px; background:#059669; border:1px solid #34d399; color:#fff; padding:5px 10px; border-radius:4px; cursor:pointer; font-size:11px; font-weight:bold;">적용</button>
            </div>

            <!-- Row B: Hair Style & Facial Feature & Body Scale -->
            <div style="display:grid; grid-template-columns: 1fr 1fr 1fr; gap:6px; background:#0f172a; padding:7px; border-radius:6px; border:1px solid #1e293b;">
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:2px;">💇 헤어스타일 (투구 OFF시 노출)</div>
                <select id="select-hair-style" style="width:100%; background:#1e293b; border:1px solid #475569; color:#f8fafc; padding:3px; border-radius:4px; font-size:10.5px;">
                  ${HAIR_STYLE_OPTIONS.map((h) => `<option value="${h.id}" ${app.hairStyle === h.id ? 'selected' : ''}>${h.name}</option>`).join('')}
                </select>
              </div>
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:2px;">🧔 얼굴 특징 (수염/흉터/문신)</div>
                <select id="select-facial-feature" style="width:100%; background:#1e293b; border:1px solid #475569; color:#f8fafc; padding:3px; border-radius:4px; font-size:10.5px;">
                  ${FACIAL_FEATURE_OPTIONS.map((f) => `<option value="${f.id}" ${app.facialFeature === f.id ? 'selected' : ''}>${f.name}</option>`).join('')}
                </select>
              </div>
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:2px;">📐 체형 크기: <b style="color:#4ade80;">${Math.round((app.bodyScale || 1) * 100)}%</b></div>
                <div style="display:flex; gap:3px;">
                  <button data-body-scale="0.90" style="flex:1; background:${app.bodyScale <= 0.92 ? '#0284c7' : '#1e293b'}; border:1px solid #475569; color:#fff; border-radius:3px; font-size:9.5px; padding:3px 0; cursor:pointer;">슬림</button>
                  <button data-body-scale="1.00" style="flex:1; background:${app.bodyScale > 0.92 && app.bodyScale < 1.06 ? '#0284c7' : '#1e293b'}; border:1px solid #475569; color:#fff; border-radius:3px; font-size:9.5px; padding:3px 0; cursor:pointer;">표준</button>
                  <button data-body-scale="1.12" style="flex:1; background:${app.bodyScale >= 1.06 ? '#0284c7' : '#1e293b'}; border:1px solid #475569; color:#fff; border-radius:3px; font-size:9.5px; padding:3px 0; cursor:pointer;">거구</button>
                </div>
              </div>
            </div>

            <!-- Row C: Skin Tone & Hair Color Swatches -->
            <div style="display:grid; grid-template-columns: 1fr 1fr; gap:6px; background:#0f172a; padding:6px 8px; border-radius:6px; border:1px solid #1e293b;">
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:4px;">🧑 피부 톤 (Skin Tone)</div>
                <div style="display:flex; gap:4px; flex-wrap:wrap;">
                  ${renderColorSwatches(SKIN_COLOR_PALETTE, app.skinColor, 'data-skin-color')}
                </div>
              </div>
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:4px;">🎨 머리 & 수염 색상 (Hair Color)</div>
                <div style="display:flex; gap:4px; flex-wrap:wrap;">
                  ${renderColorSwatches(HAIR_COLOR_PALETTE, app.hairColor, 'data-hair-color')}
                </div>
              </div>
            </div>

            <!-- Row D: Armor / Outfit Primary, Secondary & Cape Dye Swatches -->
            <div style="display:grid; grid-template-columns: 1fr 1fr 1fr; gap:6px; background:#0f172a; padding:6px 8px; border-radius:6px; border:1px solid #1e293b;">
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:4px;">🛡️ 갑옷/의상 메인 염색</div>
                <div style="display:flex; gap:3px; flex-wrap:wrap;">
                  ${renderColorSwatches(ARMOR_COLOR_PALETTE.slice(0, 7), app.primaryArmorColor, 'data-primary-color')}
                </div>
              </div>
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:4px;">✨ 보조/방패/장식 염색</div>
                <div style="display:flex; gap:3px; flex-wrap:wrap;">
                  ${renderColorSwatches(ARMOR_COLOR_PALETTE.slice(0, 7), app.secondaryArmorColor, 'data-secondary-color')}
                </div>
              </div>
              <div>
                <div style="font-size:10px; color:#94a3b8; margin-bottom:4px;">🧣 망토/후드/깃털 염색</div>
                <div style="display:flex; gap:3px; flex-wrap:wrap;">
                  ${renderColorSwatches(ARMOR_COLOR_PALETTE.slice(0, 7), app.capeColor, 'data-cape-color')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. 6 Terra Anima Races -->
      <div style="margin-bottom:12px; background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px;">
        <div style="font-weight:bold; color:#38bdf8; margin-bottom:6px; font-size:13px;">
          🧬 2. 테라 아니마 6대 출신 종족 선택 (초기 스탯 합계 420점 동일 & 다옥식 직업 제한)
        </div>
        <div style="display:grid; grid-template-columns:repeat(3, 1fr); gap:6px; margin-bottom:8px;">
    `;

    for (const rKey of Object.keys(TERRA_ANIMA_RACES) as RaceId[]) {
      const r = TERRA_ANIMA_RACES[rKey];
      const isSelected = rKey === curRace;
      const canPlayCurrentRole = r.allowedRoles.includes(player.role);

      html += `
        <div data-select-race="${rKey}" style="
          background:${isSelected ? 'rgba(14, 116, 144, 0.35)' : 'rgba(15, 23, 42, 0.8)'};
          border:1.5px solid ${isSelected ? '#38bdf8' : canPlayCurrentRole ? '#334155' : '#7f1d1d'};
          border-radius:6px;
          padding:7px;
          cursor:pointer;
          opacity:${canPlayCurrentRole ? '1' : '0.6'};
        ">
          <div style="display:flex; justify-content:space-between; align-items:center; font-weight:bold; color:${isSelected ? '#38bdf8' : '#f8fafc'};">
            <span>${r.nameKr}</span>
            <span style="font-size:10px; color:${canPlayCurrentRole ? '#4ade80' : '#f87171'};">
              ${canPlayCurrentRole ? '⭕ 선택가능' : '❌ 직업제한'}
            </span>
          </div>
          <div style="font-size:10px; color:#94a3b8; margin-top:3px;">
            힘${r.baseStats.str} 체${r.baseStats.con} 민${r.baseStats.dex} 속${r.baseStats.qui} 지${r.baseStats.int} 신${r.baseStats.pie} 매${r.baseStats.cha}
          </div>
        </div>
      `;
    }

    html += `
        </div>
        <div style="font-size:11px; color:#cbd5e1; background:#0f172a; padding:8px; border-radius:6px; border:1px solid #1e293b;">
          📍 <b>${raceDef.nameKr}</b> (${raceDef.originZone}): ${raceDef.description}
        </div>
      </div>

      <!-- 30 Bonus Point Allocator -->
      <div style="background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div>
            <span style="font-weight:bold; color:#f59e0b; font-size:13px;">🎲 2. 시작 보너스 30 포인트 가중치 배분</span>
            <div style="font-size:10px; color:#94a3b8;">(+1~+10: 1점당 1p / +11~+15: 1점당 2p / +16~+18: 1점당 3p)</div>
          </div>
          <div style="display:flex; align-items:center; gap:6px;">
            <span style="background:#0f172a; border:1px solid #f59e0b; color:#fde047; padding:3px 8px; border-radius:6px; font-weight:bold;">
              남은 포인트: ${remPts} / 30p
            </span>
            <button id="btn-preset-10-10-10" style="background:#1e293b; border:1px solid #475569; color:#38bdf8; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">[10/10/10] 정석</button>
            <button id="btn-preset-15-10" style="background:#1e293b; border:1px solid #475569; color:#4ade80; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">[15/10] 특화</button>
            <button id="btn-reset-bonus" style="background:#7f1d1d; border:1px solid #f87171; color:#fff; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:10px;">초기화</button>
          </div>
        </div>

        <table style="width:100%; border-collapse:collapse; font-size:11px;">
          <thead>
            <tr style="background:#0f172a; color:#94a3b8; text-align:left;">
              <th style="padding:6px;">스탯</th>
              <th style="padding:6px;">종족 기본</th>
              <th style="padding:6px;">보너스 투자</th>
              <th style="padding:6px;">소모 비용</th>
              <th style="padding:6px;">시작 합계</th>
              <th style="padding:6px;">핵심 효과</th>
            </tr>
          </thead>
          <tbody>
    `;

    const statRows: Array<{ key: keyof StatBlock; label: string; desc: string }> = [
      { key: 'str', label: '힘 (STR)', desc: '슬래시/크러시 딜·치명타, 소지무게' },
      { key: 'con', label: '체력 (CON)', desc: '최대 HP(+8) 및 최대 SP(+2)' },
      { key: 'dex', label: '민첩 (DEX)', desc: '블럭/패리/이베이드 회피율, 쓰러스트 치명타' },
      { key: 'qui', label: '속도 (QUI)', desc: '무기 공속 + 모든 주문 시전속도 단축' },
      { key: 'int', label: '지능 (INT)', desc: '마법사/레인저 MP(+5) 및 최대 집중력(+1)' },
      { key: 'pie', label: '신앙 (PIE)', desc: '클레릭/수도승/팔라딘 MP(+5) 및 집중력(+1)' },
      { key: 'cha', label: '매력 (CHA)', desc: '바드 MP(+5) 및 집중력(+1), 메즈/흥정' },
    ];

    for (const row of statRows) {
      const baseVal = raceDef.baseStats[row.key];
      const addVal = bonus[row.key] || 0;
      const costVal = getBonusStatPointCost(addVal);
      const nextCost = addVal < 10 ? 1 : addVal < 15 ? 2 : 3;
      const canAdd = addVal < 18 && remPts >= nextCost;

      html += `
        <tr style="border-bottom:1px solid #1e293b;">
          <td style="padding:6px; font-weight:bold; color:#f8fafc;">${row.label}</td>
          <td style="padding:6px; color:#cbd5e1;">${baseVal}</td>
          <td style="padding:6px;">
            <button data-bonus-minus="${row.key}" style="background:#334155; border:none; color:#fff; width:20px; height:20px; border-radius:4px; cursor:pointer;">-</button>
            <span style="display:inline-block; width:34px; text-align:center; font-weight:bold; color:#fde047;">+${addVal}</span>
            <button data-bonus-plus="${row.key}" ${!canAdd ? 'disabled' : ''} style="background:${canAdd ? '#0284c7' : '#1e293b'}; border:none; color:#fff; width:20px; height:20px; border-radius:4px; cursor:pointer;">+</button>
          </td>
          <td style="padding:6px; color:#94a3b8;">${costVal}p <span style="font-size:9px;">(다음:${nextCost}p)</span></td>
          <td style="padding:6px; font-weight:bold; color:#4ade80; font-size:13px;">${baseVal + addVal}</td>
          <td style="padding:6px; color:#94a3b8; font-size:10px;">${row.desc}</td>
        </tr>
      `;
    }

    html += `
          </tbody>
        </table>
      </div>
    `;
    return html;
  }

  // ==========================================================================
  // TAB 2: 📊 7대 스탯 실시간 수치 & HP 비례 이동속도 & 다옥 전문스킬 트리
  // ==========================================================================
  private renderStatsAndSpecTab(player: EntityState): string {
    const base = player.baseStats || { str: 60, con: 60, dex: 60, qui: 60, int: 60, pie: 60, cha: 60 };
    const eff = player.effectiveStats || base;
    const metrics = getDerivedCombatMetrics(player);
    const effMoveSpeed = getEffectiveMoveSpeed(player);
    const hpPct = Math.round((player.resources.hp / Math.max(1, player.resources.maxHp)) * 100);
    const movePct = Math.round((effMoveSpeed / (player.speed || 4.2)) * 100);

    const isAdmin = SkillEngine.isEntityAdmin(player);
    const isNovice = !isAdmin && SkillEngine.isEntityNovice(player);

    let skillSet = CLASS_SKILL_DEFINITIONS[player.role] || CLASS_SKILL_DEFINITIONS.tank;
    let maxSlots = player.role === 'monk_hybrid' ? 8 : 7;
    if (isAdmin) {
      skillSet = CLASS_SKILL_DEFINITIONS.admin;
      maxSlots = 8;
    } else if (isNovice) {
      skillSet = CLASS_SKILL_DEFINITIONS.novice;
      maxSlots = 1;
    }

    const fameVal = player.fame ?? 180;
    const fameRank = getFameRankInfo(fameVal);
    const famePct = Math.min(100, Math.max(5, Math.round((Math.max(0, fameVal) / fameRank.nextTier) * 100)));

    let html = `
      <!-- 0. 🏆 명성 수치 (Fame & Realm Renown) 스탯 패널 (퀘스트 보상, 엘리트·보스몹 사냥 획득 / 도둑질 감소) -->
      <div style="background:linear-gradient(135deg, rgba(30,41,59,0.92), rgba(15,23,42,0.95)); border:1.5px solid ${fameRank.color}; border-radius:8px; padding:10px 12px; margin-bottom:10px;">
        <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:6px;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-size:22px;">${fameRank.badge}</span>
            <div>
              <div style="font-size:13px; font-weight:bold; color:#f8fafc;">
                🏆 명성 수치 (Fame): <span style="color:${fameRank.color}; font-size:15px;">${fameVal} pt</span>
                <span style="background:rgba(0,0,0,0.45); border:1px solid ${fameRank.color}; color:${fameRank.color}; padding:1px 7px; border-radius:4px; font-size:10.5px; margin-left:6px;">
                  칭호: ${fameRank.rankTitle}
                </span>
              </div>
              <div style="font-size:10px; color:#cbd5e1; margin-top:2px;">
                ${fameRank.desc} (도둑질 적발/감행 기록: <b style="color:#f87171;">${player.theftCount ?? 0}회</b>)
              </div>
            </div>
          </div>
          ${isAdmin ? `
          <div style="display:flex; gap:4px; flex-wrap:wrap; justify-content:flex-end;">
            <button data-fame-gain="100,왕국 의뢰 퀘스트 완료 보상" style="background:#065f46; border:1px solid #34d399; color:#fff; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:9.5px; font-weight:bold;">📜 퀘스트 보상 (+100 명성)</button>
            <button data-fame-gain="150,엘리트 마수 토벌 보상" style="background:#1e3a8a; border:1px solid #60a5fa; color:#fff; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:9.5px; font-weight:bold;">👹 엘리트 사냥 (+150 명성)</button>
            <button data-fame-gain="250,월드 보스몹 토벌 보상" style="background:#7c2d12; border:1px solid #fb923c; color:#fde047; padding:3px 7px; border-radius:4px; cursor:pointer; font-size:9.5px; font-weight:bold;">🐲 보스몹 사냥 (+250 명성)</button>
          </div>
          ` : ''}
        </div>
        <div style="width:100%; height:6px; background:#090d16; border-radius:3px; overflow:hidden; border:1px solid #334155;">
          <div style="width:${famePct}%; height:100%; background:${fameRank.color}; transition:width 0.2s;"></div>
        </div>
      </div>

      <!-- Top Summary: 7 Stats + Derived Combat Metrics + HP Speed Formula -->
      <div style="display:grid; grid-template-columns:1.1fr 1fr; gap:10px; margin-bottom:12px;">
        <div style="background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px;">
          <div style="font-weight:bold; color:#38bdf8; margin-bottom:6px;">📊 7대 스탯 + 명성 (순수 + 수도승 기본버프 + 클레릭 특화버프 + 인챈트)</div>
          <div style="display:grid; grid-template-columns:repeat(2, 1fr); gap:4px; font-size:11px;">
            <div>💪 힘(STR): <b style="color:#4ade80;">${eff.str}</b> <span style="color:#64748b;">(순수 ${base.str})</span></div>
            <div>❤️ 체력(CON): <b style="color:#4ade80;">${eff.con}</b> <span style="color:#64748b;">(순수 ${base.con})</span></div>
            <div>🪶 민첩(DEX): <b style="color:#4ade80;">${eff.dex}</b> <span style="color:#64748b;">(순수 ${base.dex})</span></div>
            <div>⚡ 속도(QUI): <b style="color:#4ade80;">${eff.qui}</b> <span style="color:#64748b;">(순수 ${base.qui})</span></div>
            <div>📘 지능(INT): <b style="color:#4ade80;">${eff.int}</b> <span style="color:#64748b;">(순수 ${base.int})</span></div>
            <div>🙏 신앙(PIE): <b style="color:#4ade80;">${eff.pie}</b> <span style="color:#64748b;">(순수 ${base.pie})</span></div>
            <div>🎵 매력(CHA): <b style="color:#4ade80;">${eff.cha}</b> <span style="color:#64748b;">(순수 ${base.cha})</span></div>
            <div>🏆 명성(FAME): <b style="color:${fameRank.color};">${fameVal} pt</b> <span style="color:#fde047;">(${fameRank.badge}${fameRank.rankTitle.split(' (')[0]})</span></div>
          </div>
        </div>

        <div style="background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px;">
          <div style="font-weight:bold; color:#f59e0b; margin-bottom:6px;">🏃 HP 비례 이동속도 & 파생 전투 수치</div>
          <div style="font-size:11px; line-height:1.5;">
            <div>• 현재 HP 비율: <b style="color:${hpPct > 50 ? '#4ade80' : '#f87171'};">${hpPct}%</b> ➔ 이동속도: <b style="color:#fde047;">${movePct}% (${effMoveSpeed.toFixed(2)}타일/s)</b></div>
            ${isAdmin ? `
            <div style="display:flex; gap:4px; margin:4px 0;">
              <button data-test-hp="1.0" style="background:#065f46; border:1px solid #34d399; color:#fff; padding:2px 6px; border-radius:4px; cursor:pointer; font-size:10px;">만피(100%)</button>
              <button data-test-hp="0.7" style="background:#9a3412; border:1px solid #fb923c; color:#fff; padding:2px 6px; border-radius:4px; cursor:pointer; font-size:10px;">경상(70%)</button>
              <button data-test-hp="0.3" style="background:#7f1d1d; border:1px solid #f87171; color:#fff; padding:2px 6px; border-radius:4px; cursor:pointer; font-size:10px;">중상(30% 도주불가)</button>
            </div>
            ` : ''}
            <div>• 물리/마법 딜보정: <b>x${metrics.physDamageMult.toFixed(2)}</b> / <b>x${metrics.magicDamageMult.toFixed(2)}</b></div>
            <div>• 치명타율(물리/마법): <b>${(metrics.physCritChance * 100).toFixed(1)}%</b> / <b>${(metrics.magicCritChance * 100).toFixed(1)}%</b></div>
            <div>• 공속/시전 단축: <b>-${Math.round((1 - metrics.swingTimeMult) * 100)}%</b> / 회피율: <b>${(metrics.evadeChance * 100).toFixed(1)}%</b></div>
          </div>
        </div>
      </div>

      <!-- DAoC Spec Skill Point Investment Table -->
      <div style="background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div>
            <span style="font-weight:bold; color:#fde047; font-size:13px;">⚔️ 전문스킬 트리 (다옥식 누적 포인트: L레벨 달성 시 L포인트 소모)</span>
            <div style="font-size:10px; color:#94a3b8;">장비 인챈트 보너스는 최대 +10까지 합산(최대 유효 60레벨)되며, 시전 단수(Down-ranking)를 낮춰 자원을 아낄 수 있습니다.</div>
          </div>
          <div style="display:flex; align-items:center; gap:6px;">
            <span style="background:#0f172a; border:1px solid #38bdf8; color:#38bdf8; padding:3px 8px; border-radius:6px; font-weight:bold;">
              남은 스킬P: ${player.specPointsAvailable || 0}p
            </span>
            ${isAdmin ? `
            <button id="btn-char-lv-up" style="background:#0284c7; border:1px solid #38bdf8; color:#fff; padding:3px 8px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">캐릭터 +5레벨업</button>
            <button id="btn-char-lv-50" style="background:#b45309; border:1px solid #f59e0b; color:#fff; padding:3px 8px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">50만렙 달성</button>
            ` : ''}
          </div>
        </div>

        <table style="width:100%; border-collapse:collapse; font-size:11px;">
          <thead>
            <tr style="background:#0f172a; color:#94a3b8; text-align:left;">
              <th style="padding:6px;">슬롯 / 스킬명</th>
              <th style="padding:6px;">습득 레벨 (최대 50)</th>
              <th style="padding:6px;">인챈트(+10캡)</th>
              <th style="padding:6px;">시전단수 (Down-rank)</th>
              <th style="padding:6px;">유효레벨 & 현재 소모/성능</th>
            </tr>
          </thead>
          <tbody>
    `;

    for (let s = 1; s <= maxSlots; s++) {
      const def = skillSet[s];
      if (!def) continue;
      const trained = player.specSkillLevels?.[s] ?? 1;
      const castLv = player.selectedCastLevels?.[s] ?? trained;
      const equipB = Math.min(10, player.equipSkillBonuses?.[s] ?? 0);
      const effLv = getEffectiveSkillLevel(player, s);
      const scaled = getScaledSkillMetrics(def, effLv);
      const nextLvCost = trained + 1;
      const canTrain = trained < 50 && (player.specPointsAvailable || 0) >= nextLvCost;

      html += `
        <tr style="border-bottom:1px solid #1e293b;">
          <td style="padding:6px; font-weight:bold; color:#f8fafc;">
            [${s}] ${def.icon} ${def.nameKr}
          </td>
          <td style="padding:6px;">
            <span style="font-weight:bold; color:#fde047; display:inline-block; width:36px;">Lv.${trained}</span>
            <button data-train-skill="${s}" ${!canTrain ? 'disabled' : ''} style="background:${canTrain ? '#0284c7' : '#1e293b'}; border:1px solid ${canTrain ? '#38bdf8' : '#334155'}; color:#fff; padding:2px 6px; border-radius:4px; cursor:pointer; font-size:10px;">
              +1렙 (${nextLvCost}p)
            </button>
          </td>
          <td style="padding:6px; font-weight:bold; color:${equipB > 0 ? '#4ade80' : '#64748b'};">
            +${equipB} / 10
          </td>
          <td style="padding:6px;">
            <button data-castlv-minus="${s}" style="background:#334155; border:none; color:#fff; width:18px; height:18px; border-radius:3px; cursor:pointer;">-</button>
            <span style="display:inline-block; width:38px; text-align:center; color:#38bdf8; font-weight:bold;">${castLv}단</span>
            <button data-castlv-plus="${s}" style="background:#334155; border:none; color:#fff; width:18px; height:18px; border-radius:3px; cursor:pointer;">+</button>
          </td>
          <td style="padding:6px; font-size:10px; color:#cbd5e1;">
            <b style="color:#38bdf8;">유효 Lv.${effLv}</b> | 수치 <b>${scaled.power}</b> |
            ${scaled.spCost > 0 ? `${scaled.spCost}SP ` : ''}${scaled.mpCost > 0 ? `${scaled.mpCost}MP ` : ''}${scaled.concCost > 0 ? `${scaled.concCost}집중 ` : ''}| 쿨 ${scaled.cooldown}s
          </td>
        </tr>
      `;
    }

    html += `
          </tbody>
        </table>
      </div>
    `;
    return html;
  }

  // ==========================================================================
  // TAB 3: 🛠️ 자율 습득 스킬 19종 (전투 3 / 연습 4 / 생활 8 / 공통 4)
  // ==========================================================================
  private renderSelfSkillsTab(player: EntityState): string {
    const isAdmin = SkillEngine.isEntityAdmin(this.registry.getPlayer() || player);
    const skills = player.selfSkills || ({} as SelfSkillBlock);
    const categories: Array<{ id: 'combat' | 'practice' | 'life' | 'common'; title: string; color: string }> = [
      { id: 'combat', title: '⚔️ a. 전투스킬 (무기 데미지 증가 + SP 소모 감소 + 제작 시너지)', color: '#f87171' },
      { id: 'practice', title: '📚 b. 연습스킬 (전용 시설 수련 -> 직업 효율 증가 + 주 스탯 상승)', color: '#38bdf8' },
      { id: 'life', title: '⚒️ c. 생활스킬 8종 (채집 · 부품 가공 · 완제품 조립 + 스탯 상승)', color: '#4ade80' },
      { id: 'common', title: '🎭 d. 공통스킬 4종 (화술 · 공연 · 구걸[비밀소문] · 훔치기)', color: '#fde047' },
    ];

    let html = '';
    for (const cat of categories) {
      const list = SELF_SKILL_METAS.filter((m) => m.category === cat.id);
      html += `
        <div style="margin-bottom:10px; background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px;">
          <div style="font-weight:bold; color:${cat.color}; margin-bottom:6px; font-size:12px;">${cat.title}</div>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:6px;">
      `;
      for (const m of list) {
        const val = skills[m.key] || 0;
        html += `
          <div style="background:#0f172a; border:1px solid #1e293b; border-radius:6px; padding:7px; display:flex; flex-direction:column; justify-content:space-between;">
            <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
              <span style="font-weight:bold; color:#f8fafc;">${m.icon} ${m.nameKr}</span>
              <span style="font-weight:bold; color:#fde047;">${val.toFixed(1)}%</span>
            </div>
            <div style="background:#1e293b; height:5px; border-radius:3px; overflow:hidden; margin-bottom:4px;">
              <div style="width:${val}%; height:100%; background:${cat.color};"></div>
            </div>
            <div style="font-size:10px; color:#94a3b8; margin-bottom:5px;">${m.description}</div>
            <div style="display:flex; justify-content:space-between; align-items:center;">
              <span style="font-size:9px; color:#64748b;">${m.facilityOrTool}</span>
              ${isAdmin ? `
              <button data-practice-self="${m.key}" style="background:#1e293b; border:1px solid #38bdf8; color:#38bdf8; padding:2px 8px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">
                수련/실행 (+2.5%)
              </button>
              ` : ''}
            </div>
          </div>
        `;
      }
      html += `</div></div>`;
    }
    return html;
  }

  // ==========================================================================
  // TAB 4: ⚒️ 멀티스킬 부품 조립(칼날+칼자루+칼집) & 🔮 인챈터(+10 캡) 공방
  // ==========================================================================
  private renderCraftAndEnchanterTab(player: EntityState): string {
    const isAdmin = SkillEngine.isEntityAdmin(this.registry.getPlayer() || player);
    let html = `
      <div style="background:rgba(30,41,59,0.6); border:1px solid #334155; border-radius:8px; padding:10px; margin-bottom:10px;">
        <div style="font-weight:bold; color:#fde047; font-size:13px; margin-bottom:4px;">
          ⚒️ 1. 멀티스킬 부품 분업 조립 (주 부품 60% + 보조1 25% + 보조2 15%)
        </div>
        <div style="font-size:11px; color:#94a3b8; margin-bottom:8px;">
          예: 칼 제작 시 <b>칼날(메탈워킹: 공격력)</b> + <b>칼자루(우드워킹: 공속/SP절감)</b> + <b>칼집(레더워킹: 내구도/무게)</b>의 퀄리티가 각각 다른 옵션에 영향을 줍니다.
        </div>

        <div style="display:grid; grid-template-columns:1fr; gap:6px;">
    `;

    for (const rec of MULTI_SKILL_RECIPES) {
      html += `
        <div style="background:#0f172a; border:1px solid #1e293b; border-radius:6px; padding:8px; display:flex; justify-content:space-between; align-items:center;">
          <div>
            <div style="font-weight:bold; color:#f8fafc; font-size:12px;">${rec.name} <span style="font-size:10px; color:#38bdf8;">[${rec.slot.toUpperCase()}]</span></div>
            <div style="font-size:10px; color:#cbd5e1; margin-top:2px;">
              ① <b>${rec.primaryPart.name}</b>(${rec.primaryPart.skill}) +
              ② <b>${rec.secondaryPart1.name}</b>(${rec.secondaryPart1.skill}) +
              ③ <b>${rec.secondaryPart2.name}</b>(${rec.secondaryPart2.skill})
            </div>
          </div>
          <div style="display:flex; gap:6px;">
            ${isAdmin ? `
            <button data-craft-normal="${rec.id}" style="background:#1e293b; border:1px solid #475569; color:#f8fafc; padding:4px 8px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">
              ⚒️ 일반 조립
            </button>
            <button data-craft-master="${rec.id}" style="background:linear-gradient(135deg, #b45309, #d97706); border:1px solid #fde047; color:#fff; padding:4px 8px; border-radius:4px; cursor:pointer; font-size:10px; font-weight:bold;">
              🌟 99~100% 명작 조립
            </button>
            ` : `
            <span style="font-size:10px; color:#64748b; padding:4px 8px;">제작대 필요</span>
            `}
          </div>
        </div>
      `;
    }

    html += `</div></div>`;

    // Enchanter NPC Workshop Box
    const item = this.latestCraftedItem;
    html += `
      <div style="background:rgba(30,41,59,0.6); border:1.5px solid #a855f7; border-radius:8px; padding:10px; margin-bottom:10px;">
        <div style="font-weight:bold; color:#c084fc; font-size:13px; margin-bottom:4px;">
          🔮 2. 인챈터(Enchanter) NPC 마법부여 공방 (장비 합산 스킬 최대 +10 제한)
        </div>
        <div style="font-size:11px; color:#94a3b8; margin-bottom:8px;">
          완성된 물건의 레벨과 퀄리티에 따라 랜덤 개수의 <b>+전문스킬</b> 및 <b>+스탯</b> 옵션을 붙여줍니다 (예: 99퀄 방패에 타운트+2, 힐링챈트+2).
        </div>
    `;

    if (!item) {
      html += `<div style="padding:12px; background:#0f172a; border-radius:6px; color:#64748b; text-align:center;">위 레시피에서 장비를 먼저 조립해 보세요!</div>`;
    } else {
      const enchListHtml =
        item.enchants.length === 0
          ? `<div style="color:#94a3b8; font-size:11px;">아직 인챈트되지 않음 — 아래 [🔮 인챈터 옵션 부여] 버튼을 눌러보세요!</div>`
          : item.enchants
              .map(
                (e) =>
                  `<span style="display:inline-block; background:rgba(168,85,247,0.25); border:1px solid #c084fc; color:#f8fafc; padding:3px 8px; border-radius:4px; margin-right:6px; font-weight:bold;">✨ ${e.targetName} +${e.value}</span>`
              )
              .join('');

      html += `
        <div style="background:#0f172a; border:1px solid #334155; border-radius:6px; padding:10px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
            <span style="font-size:14px; font-weight:bold; color:#fde047;">🌟 ${item.name}</span>
            <span style="font-size:11px; color:#38bdf8;">아이템 레벨 Lv.${item.itemLevel} | 종합 퀄리티 <b>${item.quality}%</b></span>
          </div>
          <div style="font-size:11px; color:#cbd5e1; margin-bottom:6px; line-height:1.5;">
            • 주 부품 [${item.components.primary.name} <b>${item.components.primary.quality}%</b>] ➔ 기본 공격/방어 배율: <b>x${item.attackPowerMod}</b><br>
            • 보조1 [${item.components.secondary1.name} <b>${item.components.secondary1.quality}%</b>] ➔ 공속/시전 & SP절감 보정: <b>${item.speedSpMod >= 0 ? '+' : ''}${Math.round(item.speedSpMod * 100)}%</b><br>
            • 보조2 [${item.components.secondary2.name} <b>${item.components.secondary2.quality}%</b>] ➔ 내구도 배율: <b>x${item.durabilityMod}</b> (무게 ${item.weightKg}kg)
          </div>
          <div style="margin:8px 0; padding:8px; background:rgba(15,23,42,0.9); border-radius:4px; border:1px dashed #64748b;">
            ${enchListHtml}
          </div>
          <div style="display:flex; gap:8px;">
            ${item.enchants.length > 0 ? `
              <button disabled style="background:#334155; border:1px solid #475569; color:#94a3b8; padding:6px 12px; border-radius:6px; cursor:not-allowed; font-weight:bold; font-size:11px;">
                🔒 이미 인챈트 완료 (재인챈트 불가)
              </button>
            ` : `
              <button id="btn-run-enchanter" style="background:linear-gradient(135deg, #7e22ce, #9333ea); border:1px solid #d8b4fe; color:#fff; padding:6px 12px; border-radius:6px; cursor:pointer; font-weight:bold; font-size:11px;">
                🔮 인챈터에게 마법부여 (비용: 100G)
              </button>
            `}
            <button id="btn-equip-crafted" style="background:#065f46; border:1px solid #34d399; color:#fff; padding:6px 12px; border-radius:6px; cursor:pointer; font-weight:bold; font-size:11px;">
              🛡️ 현재 캐릭터(${player.name})에게 장착!
            </button>
          </div>
        </div>
      `;
    }

    html += `</div>`;
    return html;
  }

  // ==========================================================================
  // TAB 5: 🗺️ 테라 아니마 7대 오픈월드 존 이동 & 맵 에디터/뷰어 연동
  // ==========================================================================
  private renderWorldMapTab(player: EntityState): string {
    const sel = this.mapZoneSel ?? this.currentZoneId;
    const selName = WORLD_MAP_ZONES.find((z) => z.id === sel)?.name ?? `존 ${sel}`;
    const here = sel === this.currentZoneId;
    const rows = WORLD_MAP_ZONES.map((z) => {
      const cur = z.id === this.currentZoneId;
      const on = z.id === sel;
      return `<button data-map-zone="${z.id}" style="text-align:left;background:${on ? '#5a431f' : '#0c0905'};border:1px solid ${on ? '#f3d98b' : '#3f2f17'};color:${on ? '#fff' : '#cdb98a'};padding:5px 7px;border-radius:3px;cursor:pointer;font-size:11px;font-weight:bold;">${cur ? '📍 ' : ''}${z.name}</button>`;
    }).join('');
    return `
      <div style="display:flex;gap:10px;align-items:flex-start;">
        <div style="width:150px;flex-shrink:0;display:flex;flex-direction:column;gap:4px;">${rows}</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:12px;color:#f3d98b;font-weight:bold;margin-bottom:4px;">
            ${selName}
            <span id="wm-pos" style="font-weight:normal;color:#cdb98a;font-size:11px;margin-left:8px;">${here ? `현재 위치: (${Math.round(player.x)}, ${Math.round(player.y)})` : ''}</span>
          </div>
          <div style="position:relative;width:100%;aspect-ratio:1/1;background:#000;border:1px solid #5a431f;">
            <canvas id="wm-base" width="480" height="480" style="position:absolute;inset:0;width:100%;height:100%;image-rendering:pixelated;"></canvas>
            <canvas id="wm-over" width="480" height="480" style="position:absolute;inset:0;width:100%;height:100%;"></canvas>
          </div>
          <div style="font-size:10px;color:#8a7a55;margin-top:4px;">내 위치(●)는 현재 있는 존에서만 표시됩니다.</div>
        </div>
      </div>`;
  }

  /** 2D 평면 지도 그리기 + 내 위치 실시간 표시 */
  private setupWorldMap(): void {
    if (!this.modalEl) return;
    const base = this.modalEl.querySelector('#wm-base') as HTMLCanvasElement | null;
    const over = this.modalEl.querySelector('#wm-over') as HTMLCanvasElement | null;
    if (!base || !over) return;
    const zid = this.mapZoneSel ?? this.currentZoneId;
    let zone = worldMapZoneCache.get(zid);
    if (!zone) {
      zone = getZoneData(zid);
      worldMapZoneCache.set(zid, zone);
    }
    const S = base.width / zone.width;

    let cached = this.mapBaseCache.get(zid);
    if (!cached) {
      cached = document.createElement('canvas');
      cached.width = base.width;
      cached.height = base.height;
      const cctx = cached.getContext('2d')!;
      for (let y = 0; y < zone.height; y++) {
        const row = zone.tiles[y];
        if (!row) continue;
        for (let x = 0; x < zone.width; x++) {
          cctx.fillStyle = tileColor(row[x] || 'grass');
          cctx.fillRect(x * S, y * S, S + 0.5, S + 0.5);
        }
      }
      this.mapBaseCache.set(zid, cached);
    }
    base.getContext('2d')!.drawImage(cached, 0, 0);

    const drawOverlay = (): void => {
      const ctx = over.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, over.width, over.height);
      ctx.font = 'bold 11px sans-serif';
      ctx.textBaseline = 'middle';
      for (const lm of zone!.landmarks) {
        const lx = lm.x * S;
        const ly = lm.y * S;
        ctx.fillStyle = '#f3d98b';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(lx, ly, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.lineWidth = 3;
        ctx.strokeText(lm.name, lx + 7, ly);
        ctx.fillStyle = '#fff';
        ctx.fillText(lm.name, lx + 7, ly);
      }
      const p = this.registry.getPlayer();
      const posEl = this.modalEl?.querySelector('#wm-pos');
      if (p && zid === this.currentZoneId) {
        const px = p.x * S;
        const py = p.y * S;
        const pulse = 6 + 3 * Math.sin(performance.now() / 250);
        ctx.strokeStyle = 'rgba(255,60,60,0.8)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, py, pulse, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#ff3b3b';
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, py, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        if (posEl) posEl.textContent = `현재 위치: (${Math.round(p.x)}, ${Math.round(p.y)})`;
      } else if (posEl) {
        posEl.textContent = '';
      }
    };
    drawOverlay();

    if (this.mapTimer !== null) window.clearInterval(this.mapTimer);
    this.mapTimer = window.setInterval(() => {
      if (!this.isOpen || this.activeTab !== 'world_map' || !over.isConnected) {
        if (this.mapTimer !== null) window.clearInterval(this.mapTimer);
        this.mapTimer = null;
        return;
      }
      drawOverlay();
    }, 250);

    this.modalEl.querySelectorAll<HTMLElement>('[data-map-zone]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.mapZoneSel = parseInt(btn.dataset.mapZone || '1', 10);
        this.render();
      });
    });
  }

  // ==========================================================================
  // TAB: 🌟 [영혼의 성소] 12세 성인식 8문 8답 시험 & 8대 성격 파라미터 엔진
  // ==========================================================================
  private renderPersonalityTab(player: EntityState): string {
    const partyList = this.registry.getParty();
    if (!this.selectedPersonalityCharId || !partyList.some((p) => p.id === this.selectedPersonalityCharId)) {
      this.selectedPersonalityCharId = player.id;
    }
    const curChar = partyList.find((p) => p.id === this.selectedPersonalityCharId) || player;

    // Character switch bar
    let charSelectorHtml = `
      <div style="display:flex; gap:6px; margin-bottom:12px; overflow-x:auto; padding-bottom:4px;">
    `;
    for (const c of partyList) {
      const isCur = c.id === curChar.id;
      const titleShort = c.personality?.soulTitle ? c.personality.soulTitle.split(' (')[0] : '미각인';
      charSelectorHtml += `
        <button data-select-personality-char="${c.id}" style="
          display:flex; align-items:center; gap:6px; padding:6px 12px; border-radius:6px; cursor:pointer;
          background:${isCur ? 'linear-gradient(135deg, #1e3a8a, #2563eb)' : '#1e293b'};
          border:1.5px solid ${isCur ? '#60a5fa' : '#334155'};
          color:${isCur ? '#ffffff' : '#94a3b8'};
          font-size:11px; white-space:nowrap;
        ">
          <span style="font-size:15px;">${c.avatarIcon}</span>
          <span style="font-weight:bold;">${c.name}</span>
          <span style="font-size:9.5px; color:${isCur ? '#fde047' : '#64748b'};">【${titleShort}】</span>
        </button>
      `;
    }
    charSelectorHtml += `</div>`;

    // Lore banner explaining 4 Suns, 2 Moons, 10 Seasons = 1 Year, 12-Year Rite of Passage
    const loreBannerHtml = `
      <div style="background:linear-gradient(135deg, rgba(30, 27, 75, 0.9), rgba(15, 23, 42, 0.95)); border:1.5px solid #818cf8; border-radius:8px; padding:12px 16px; margin-bottom:14px; box-shadow:0 4px 14px rgba(0,0,0,0.4);">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:6px;">
          <div style="font-size:13px; font-weight:bold; color:#fbbf24; display:flex; align-items:center; gap:6px;">
            <span>☀️☀️☀️☀️ 🌙🌙 솔라리아 4태양·2달 연대기 & [영혼의 성소] 12세 성인식</span>
          </div>
          <span style="font-size:10px; color:#c7d2fe; background:rgba(99,102,241,0.25); padding:2px 8px; border-radius:4px; border:1px solid rgba(99,102,241,0.5); font-weight:bold;">
            10계절 순환 = 행성 나이 1살 (현실 누적 1년)
          </span>
        </div>
        <div style="font-size:11px; color:#cbd5e1; line-height:1.6;">
          솔라리스 왕국은 4개의 태양(솔-프라임, 아우라, 이그니스, 루멘)과 2개의 달(루나, 마나)이 공전하는 4중 항성계에 위치합니다.
          모든 아이는 12세가 되는 해 [영혼의 성소]에서 고위 사제의 8문 8답 시험을 거쳐 성인이 되며,
          자신의 타고난 영혼 원형(MBTI)과 가치관(Alignment), 그리고 운명의 칭호를 각인받습니다.
        </div>
      </div>
    `;

    if (this.isPersonalityQuizMode) {
      return `
        ${charSelectorHtml}
        ${loreBannerHtml}
        ${this.renderQuizStepper(curChar)}
      `;
    }

    return `
      ${charSelectorHtml}
      ${loreBannerHtml}
      ${this.renderPersonalityProfile(curChar)}
    `;
  }

  private renderQuizStepper(curChar: EntityState): string {
    const q = PERSONALITY_QUESTIONS[this.quizStep];
    const curAnswer = this.quizSelectedAnswers[this.quizStep] ?? 0;

    let stepDotsHtml = `<div style="display:flex; justify-content:center; gap:6px; margin-bottom:14px;">`;
    for (let i = 0; i < PERSONALITY_QUESTIONS.length; i++) {
      const isCurrent = i === this.quizStep;
      const isPast = i < this.quizStep;
      stepDotsHtml += `
        <div style="
          padding:4px 8px; border-radius:4px; font-size:10.5px; font-weight:bold;
          background:${isCurrent ? '#f59e0b' : isPast ? '#065f46' : '#1e293b'};
          color:${isCurrent ? '#000' : isPast ? '#a7f3d0' : '#64748b'};
          border:1px solid ${isCurrent ? '#fde047' : isPast ? '#10b981' : '#334155'};
        ">
          Q${i + 1} ${PERSONALITY_QUESTIONS[i].axisNameKr.split(' ')[0]}
        </div>
      `;
    }
    stepDotsHtml += `</div>`;

    let choicesHtml = `<div style="display:flex; flex-direction:column; gap:8px; margin:14px 0;">`;
    const labels = ['A', 'B', 'C', 'D'];
    q.options.forEach((opt, idx) => {
      const isSel = curAnswer === idx;
      choicesHtml += `
        <button data-quiz-choice="${idx}" style="
          display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:6px; cursor:pointer; text-align:left;
          background:${isSel ? 'linear-gradient(135deg, #1e3a8a, #1d4ed8)' : 'rgba(30,41,59,0.7)'};
          border:1.5px solid ${isSel ? '#60a5fa' : '#334155'};
          color:${isSel ? '#ffffff' : '#e2e8f0'};
          box-shadow:${isSel ? '0 0 12px rgba(59,130,246,0.5)' : 'none'};
          transition:all 0.15s;
        ">
          <span style="
            display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px; border-radius:50%;
            background:${isSel ? '#f59e0b' : '#334155'}; color:${isSel ? '#000' : '#f8fafc'}; font-weight:bold; font-size:11px;
          ">${labels[idx]}</span>
          <span style="font-size:12px; line-height:1.4; flex:1;">${opt.text}</span>
          ${isSel ? '<span style="color:#fde047; font-weight:bold; font-size:11px;">✓ 선택됨</span>' : ''}
        </button>
      `;
    });
    choicesHtml += `</div>`;

    const isLast = this.quizStep === PERSONALITY_QUESTIONS.length - 1;

    return `
      <div style="background:rgba(15,23,42,0.9); border:1.5px solid #d97706; border-radius:10px; padding:16px; box-shadow:0 8px 24px rgba(0,0,0,0.6);">
        ${stepDotsHtml}
        
        <div style="background:rgba(30,41,59,0.5); border-left:4px solid #f59e0b; padding:10px 14px; border-radius:4px; margin-bottom:12px;">
          <div style="font-size:11px; color:#f59e0b; font-weight:bold; margin-bottom:4px;">
            🏛️ [영혼의 성소 대사제] 제 ${this.quizStep + 1}문: ${q.axisNameKr}
          </div>
          <div style="font-size:13px; color:#f8fafc; font-weight:600; line-height:1.5;">
            "${q.question}"
          </div>
        </div>

        ${choicesHtml}

        <div style="display:flex; justify-content:space-between; align-items:center; margin-top:16px; padding-top:12px; border-top:1px solid #334155;">
          <button id="btn-quiz-cancel" style="background:#334155; border:1px solid #475569; color:#94a3b8; padding:6px 14px; border-radius:6px; cursor:pointer; font-size:11px; font-weight:bold;">
            ✕ 시험 중단
          </button>
          
          <div style="display:flex; gap:8px;">
            ${this.quizStep > 0 ? `
              <button id="btn-quiz-prev" style="background:#1e293b; border:1px solid #38bdf8; color:#38bdf8; padding:6px 14px; border-radius:6px; cursor:pointer; font-size:11px; font-weight:bold;">
                ◀ 이전 질문
              </button>
            ` : ''}

            ${!isLast ? `
              <button id="btn-quiz-next" style="background:#0284c7; border:1px solid #38bdf8; color:#fff; padding:6px 16px; border-radius:6px; cursor:pointer; font-size:11px; font-weight:bold;">
                다음 질문 ▶
              </button>
            ` : `
              <button id="btn-quiz-finish" style="background:linear-gradient(135deg, #d97706, #f59e0b); border:1.5px solid #fde047; color:#000; padding:6px 18px; border-radius:6px; cursor:pointer; font-size:11px; font-weight:bold; box-shadow:0 0 16px rgba(245,158,11,0.6);">
                ✨ [운명의 각인] 성인식 시험 완료 및 칭호 수여받기
              </button>
            `}
          </div>
        </div>
      </div>
    `;
  }

  private renderPersonalityProfile(curChar: EntityState): string {
    if (!curChar.personality) {
      return `
        <div style="text-align:center; padding:36px 16px; background:rgba(30,41,59,0.6); border:1px dashed #64748b; border-radius:10px;">
          <div style="font-size:32px; margin-bottom:8px;">🏛️</div>
          <div style="font-size:14px; font-weight:bold; color:#f8fafc; margin-bottom:4px;">
            [${curChar.name}] 님은 아직 [영혼의 성소] 12세 성인식 시험을 치르지 않았습니다.
          </div>
          <div style="font-size:11px; color:#94a3b8; margin-bottom:18px;">
            사제의 8문 8답을 통해 자신의 타고난 성향과 MBTI, 그리고 영혼의 칭호를 각인받으세요.
          </div>
          <div style="display:flex; justify-content:center; gap:10px;">
            <button id="btn-start-quiz" style="background:linear-gradient(135deg, #d97706, #f59e0b); border:1.5px solid #fde047; color:#000; padding:8px 18px; border-radius:6px; cursor:pointer; font-size:12px; font-weight:bold; box-shadow:0 0 14px rgba(245,158,11,0.5);">
              ✨ 12세 성인식 8문 8답 시험 치르기
            </button>
            <button id="btn-quick-destiny" style="background:#1e293b; border:1px solid #38bdf8; color:#38bdf8; padding:8px 14px; border-radius:6px; cursor:pointer; font-size:11px; font-weight:bold;">
              🎲 기본 성향 부여
            </button>
          </div>
        </div>
      `;
    }

    const p = curChar.personality;
    const axes = p.axes;

    // Axis definitions for visualization
    const axisMeta: Array<{
      key: keyof PersonalityAxes;
      nameKr: string;
      leftLabel: string;
      rightLabel: string;
      leftColor: string;
      rightColor: string;
    }> = [
      { key: 'courage', nameKr: '용기', leftLabel: '겁 (Fearful)', rightLabel: '용기 (Brave)', leftColor: '#ef4444', rightColor: '#f59e0b' },
      { key: 'order', nameKr: '질서', leftLabel: '혼돈 (Chaotic)', rightLabel: '질서 (Lawful)', leftColor: '#a855f7', rightColor: '#3b82f6' },
      { key: 'moral', nameKr: '도덕', leftLabel: '악 (Evil)', rightLabel: '선 (Good)', leftColor: '#dc2626', rightColor: '#10b981' },
      { key: 'materialism', nameKr: '물질욕', leftLabel: '금욕 (Ascetic)', rightLabel: '물질욕 (Greedy)', leftColor: '#06b6d4', rightColor: '#f59e0b' },
      { key: 'extraversion', nameKr: '외향성', leftLabel: '내향 (I)', rightLabel: '외향 (E)', leftColor: '#6366f1', rightColor: '#f97316' },
      { key: 'intuition', nameKr: '직관성', leftLabel: '감각 (S)', rightLabel: '직관 (N)', leftColor: '#14b8a6', rightColor: '#a855f7' },
      { key: 'thinking', nameKr: '사고성', leftLabel: '감정 (F)', rightLabel: '사고 (T)', leftColor: '#ec4899', rightColor: '#0ea5e9' },
      { key: 'perceiving', nameKr: '즉흥성', leftLabel: '판단 (J)', rightLabel: '즉흥 (P)', leftColor: '#64748b', rightColor: '#84cc16' },
    ];

    let axesGridHtml = `<div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:14px;">`;
    for (const m of axisMeta) {
      const val = axes[m.key] ?? 0;
      const isPos = val >= 0;
      const absVal = Math.abs(val);
      const widthPct = (absVal / 100) * 50; // max 50% from center

      axesGridHtml += `
        <div style="background:rgba(30,41,59,0.7); border:1px solid #334155; border-radius:6px; padding:8px 10px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <span style="font-weight:bold; color:#f8fafc; font-size:11px;">${m.nameKr}</span>
            <span style="font-size:11px; font-weight:bold; color:${isPos ? m.rightColor : m.leftColor};">
              ${val > 0 ? `+${val}` : val}
            </span>
          </div>

          <!-- Dual Center Gauge -->
          <div style="position:relative; height:12px; background:#0f172a; border-radius:6px; overflow:hidden; border:1px solid #1e293b; margin-bottom:4px;">
            <!-- Center line -->
            <div style="position:absolute; left:50%; top:0; bottom:0; width:2px; background:#475569; z-index:2;"></div>
            ${isPos ? `
              <div style="position:absolute; left:50%; top:0; bottom:0; width:${widthPct}%; background:${m.rightColor}; border-radius:0 3px 3px 0;"></div>
            ` : `
              <div style="position:absolute; right:50%; top:0; bottom:0; width:${widthPct}%; background:${m.leftColor}; border-radius:3px 0 0 3px;"></div>
            `}
          </div>

          <div style="display:flex; justify-content:space-between; font-size:9.5px; color:#94a3b8;">
            <span style="color:${val < 0 ? m.leftColor : '#64748b'};">${m.leftLabel}</span>
            <span style="color:${val > 0 ? m.rightColor : '#64748b'};">${m.rightLabel}</span>
          </div>
        </div>
      `;
    }
    axesGridHtml += `</div>`;

    // Drift action buttons
    const driftActions: Array<{ key: PersonalityActionType; label: string; desc: string; color: string }> = [
      { key: 'protect_ally', label: '🛡️ 동료 비호', desc: '+용기, +선', color: '#10b981' },
      { key: 'flee_combat', label: '🏃 전투 도주', desc: '-용기', color: '#ef4444' },
      { key: 'share_loot', label: '🤝 전리품 양보', desc: '-물질, +선', color: '#06b6d4' },
      { key: 'hoard_loot', label: '💰 전리품 독점', desc: '+물질, -선', color: '#f59e0b' },
      { key: 'uphold_law', label: '⚖️ 규율 준수', desc: '+질서', color: '#3b82f6' },
      { key: 'break_law', label: '🏴 규율 위반', desc: '-질서', color: '#a855f7' },
      { key: 'tavern_fellowship', label: '🍻 주점 연회', desc: '+외향 E', color: '#f97316' },
      { key: 'solitary_meditate', label: '🧘 홀로 명상', desc: '+내향 I, +직관 N', color: '#8b5cf6' },
      { key: 'meticulous_plan', label: '📋 철저한 계획', desc: '+판단 J', color: '#64748b' },
      { key: 'impromptu_explore', label: '🧭 즉흥 탐험', desc: '+인식 P', color: '#84cc16' },
    ];

    let driftGridHtml = `<div style="display:grid; grid-template-columns:repeat(5, 1fr); gap:6px;">`;
    for (const a of driftActions) {
      driftGridHtml += `
        <button data-drift-action="${a.key}" style="
          background:#1e293b; border:1px solid ${a.color}; border-radius:6px; padding:6px 4px; cursor:pointer;
          text-align:center; transition:all 0.15s;
        ">
          <div style="font-size:10.5px; font-weight:bold; color:#f8fafc; margin-bottom:2px;">${a.label}</div>
          <div style="font-size:9px; color:${a.color};">${a.desc}</div>
        </button>
      `;
    }
    driftGridHtml += `</div>`;

    const isAdmin = SkillEngine.isEntityAdmin(curChar);

    return `
      <!-- Soul Title & Alignment Highlight Card -->
      <div style="background:linear-gradient(135deg, rgba(30,58,138,0.7), rgba(88,28,135,0.7)); border:1.5px solid #f59e0b; border-radius:10px; padding:14px; margin-bottom:14px; box-shadow:0 8px 24px rgba(0,0,0,0.5);">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div style="display:flex; align-items:center; gap:8px;">
            <span style="font-size:24px;">${curChar.avatarIcon}</span>
            <div>
              <div style="font-size:11px; color:#93c5fd;">솔라리스 12세 성인식 공인 칭호</div>
              <div style="font-size:16px; font-weight:bold; color:#fde047; text-shadow:0 0 10px rgba(253,224,71,0.5);">
                【 ${p.soulTitle} 】
              </div>
            </div>
          </div>
          ${isAdmin ? `
          <button id="btn-retake-quiz" style="background:#1e293b; border:1px solid #f59e0b; color:#fde047; padding:4px 10px; border-radius:6px; cursor:pointer; font-size:10.5px; font-weight:bold;">
            🔄 시험 다시 치르기
          </button>
          ` : ''}
        </div>

        <div style="display:flex; gap:8px; margin-bottom:8px;">
          <div style="background:rgba(16,185,129,0.2); border:1px solid #10b981; color:#6ee7b7; padding:3px 8px; border-radius:4px; font-size:11px; font-weight:bold;">
            ⚖️ ${p.alignment}
          </div>
          <div style="background:rgba(59,130,246,0.2); border:1px solid #3b82f6; color:#93c5fd; padding:3px 8px; border-radius:4px; font-size:11px; font-weight:bold;">
            🧬 영혼 원형 MBTI: ${p.mbti}
          </div>
        </div>

        <div style="font-size:11px; color:#e2e8f0; line-height:1.5;">
          ${p.summary}
        </div>
      </div>

      <!-- 8 Personality Axes Visual Gauges -->
      <div style="margin-bottom:14px;">
        <div style="font-size:12px; font-weight:bold; color:#38bdf8; margin-bottom:8px; display:flex; align-items:center; gap:6px;">
          <span>📊 8대 성격 파라미터 게이지 (-100 ~ +100)</span>
        </div>
        ${axesGridHtml}
      </div>

      ${isAdmin ? `
      <!-- Lifestyle Drift Simulator (관리자/개발 테스트 전용) -->
      <div style="background:rgba(15,23,42,0.7); border:1px solid #334155; border-radius:8px; padding:12px;">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
          <div>
            <div style="font-size:12px; font-weight:bold; color:#f59e0b;">
              🌟 라이프스타일 성격 표류 (Lifestyle Drift) 시뮬레이터 [GM]
            </div>
            <div style="font-size:10px; color:#94a3b8;">
              인게임 선택과 행동에 따라 성격 파라미터가 유기적으로 변화하여 가치관과 칭호가 진화합니다.
            </div>
          </div>
        </div>
        ${driftGridHtml}
      </div>
      ` : ''}
    `;
  }

  private bindEvents(player: EntityState): void {
    if (this.activeTab === 'world_map') this.setupWorldMap();
    if (!this.modalEl) return;

    const closeBtn = this.modalEl.querySelector('#btn-close-inspector');
    closeBtn?.addEventListener('click', () => this.toggle());

    // Tab switches
    this.modalEl.querySelectorAll('[data-tab-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tid = btn.getAttribute('data-tab-id') as InspectorTab;
        if (tid) {
          this.activeTab = tid;
          this.render();
        }
      });
    });

    // Personality Tab Events
    this.modalEl.querySelectorAll('[data-select-personality-char]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const cId = btn.getAttribute('data-select-personality-char');
        if (cId) {
          this.selectedPersonalityCharId = cId;
          this.isPersonalityQuizMode = false;
          this.render();
        }
      });
    });

    this.modalEl.querySelector('#btn-start-quiz')?.addEventListener('click', () => {
      this.isPersonalityQuizMode = true;
      this.quizStep = 0;
      this.quizSelectedAnswers = [0, 0, 0, 0, 0, 0, 0, 0];
      this.render();
    });

    this.modalEl.querySelector('#btn-retake-quiz')?.addEventListener('click', () => {
      this.isPersonalityQuizMode = true;
      this.quizStep = 0;
      this.quizSelectedAnswers = [0, 0, 0, 0, 0, 0, 0, 0];
      this.render();
    });

    this.modalEl.querySelector('#btn-quick-destiny')?.addEventListener('click', () => {
      const curChar = this.registry.get(this.selectedPersonalityCharId || player.id) || player;
      curChar.personality = computePersonalityProfile([0, 0, 0, 1, 0, 2, 2, 0]);
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `✨ [영혼의 성소] [${curChar.name}] 님에게 성인식 기본 성향 【${curChar.personality.soulTitle}】이(가) 부여되었습니다!`,
        category: 'system',
      });
      this.render();
    });

    this.modalEl.querySelectorAll('[data-quiz-choice]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const choiceIdx = parseInt(btn.getAttribute('data-quiz-choice') || '0', 10);
        this.quizSelectedAnswers[this.quizStep] = choiceIdx;
        this.render();
      });
    });

    this.modalEl.querySelector('#btn-quiz-prev')?.addEventListener('click', () => {
      this.quizStep = Math.max(0, this.quizStep - 1);
      this.render();
    });

    this.modalEl.querySelector('#btn-quiz-next')?.addEventListener('click', () => {
      this.quizStep = Math.min(PERSONALITY_QUESTIONS.length - 1, this.quizStep + 1);
      this.render();
    });

    this.modalEl.querySelector('#btn-quiz-cancel')?.addEventListener('click', () => {
      this.isPersonalityQuizMode = false;
      this.render();
    });

    this.modalEl.querySelector('#btn-quiz-finish')?.addEventListener('click', () => {
      const curChar = this.registry.get(this.selectedPersonalityCharId || player.id) || player;
      const profile = computePersonalityProfile(this.quizSelectedAnswers);
      curChar.personality = profile;
      this.isPersonalityQuizMode = false;
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🌟 [영혼의 성소] [${curChar.name}] 님이 12세 성인식 시험을 마치고 운명의 칭호 【${profile.soulTitle}】을(를) 수여받았습니다! (${profile.alignment} · ${profile.mbti})`,
        category: 'system',
      });
      this.render();
    });

    this.modalEl.querySelectorAll('[data-drift-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const actKey = btn.getAttribute('data-drift-action') as PersonalityActionType;
        const curChar = this.registry.get(this.selectedPersonalityCharId || player.id) || player;
        if (!curChar.personality || !actKey) return;
        curChar.personality.axes = applyPersonalityDrift(curChar.personality.axes, actKey, 1);
        const align = resolveAlignment(curChar.personality.axes.order, curChar.personality.axes.moral);
        const mbti = resolveMbti(curChar.personality.axes);
        curChar.personality.alignment = align.nameKr;
        curChar.personality.mbti = mbti;
        curChar.personality.soulTitle = resolveSoulTitle(align.key, mbti);
        curChar.personality.summary = `4개의 태양이 지켜보는 [영혼의 성소]에서 각인된 운명: ${align.nameKr} 성향의 ${mbti} 영혼입니다.`;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🌟 [성격 표류] [${curChar.name}] 의 행동 (${actKey}) 으로 성향이 표류했습니다 ➔ 【${curChar.personality.soulTitle}】 (${curChar.personality.alignment} · ${curChar.personality.mbti})`,
          category: 'system',
        });
        this.render();
      });
    });

    // Live Preview Canvas Loop (for Tab 1 Customization Studio)
    if (this.previewAnimFrameId !== null) {
      cancelAnimationFrame(this.previewAnimFrameId);
      this.previewAnimFrameId = null;
    }
    const previewCanvas = this.modalEl.querySelector('#custom-preview-canvas') as HTMLCanvasElement | null;
    if (previewCanvas && this.isOpen && this.activeTab === 'race_stat') {
      const pCtx = previewCanvas.getContext('2d');
      const renderLoop = () => {
        if (!this.isOpen || this.activeTab !== 'race_stat' || !pCtx) return;
        const curPlayer = this.registry.getPlayer() || player;
        pCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
        this.spritePreviewRenderer.drawCharacterPreview(
          pCtx,
          curPlayer,
          previewCanvas.width / 2,
          previewCanvas.height * 0.72,
          2.25,
          performance.now()
        );
        this.previewAnimFrameId = requestAnimationFrame(renderLoop);
      };
      renderLoop();
    }

    // Character Name & Title Customization
    const applyNameAndTitle = () => {
      const nameInput = this.modalEl?.querySelector('#input-char-name') as HTMLInputElement | null;
      const titleSelect = this.modalEl?.querySelector('#select-char-title') as HTMLSelectElement | null;
      if (!player.appearance) player.appearance = getDefaultAppearanceForRole(player.role);
      if (nameInput && nameInput.value.trim().length > 0) {
        player.name = nameInput.value.trim();
      }
      if (titleSelect) {
        player.appearance.titlePrefix = titleSelect.value;
      }
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🎨 [캐릭터 커스터마이징] 이름 및 칭호가 <${player.appearance.titlePrefix}> [${player.name}](으)로 변경되었습니다!`,
        category: 'system',
      });
      this.render();
    };

    this.modalEl.querySelector('#btn-apply-name')?.addEventListener('click', applyNameAndTitle);
    this.modalEl.querySelector('#input-char-name')?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') applyNameAndTitle();
    });
    this.modalEl.querySelector('#select-char-title')?.addEventListener('change', applyNameAndTitle);

    // Helmet / Hat Visibility Toggle
    this.modalEl.querySelector('#btn-toggle-helm')?.addEventListener('click', () => {
      if (!player.appearance) player.appearance = getDefaultAppearanceForRole(player.role);
      player.appearance.showHelm = !player.appearance.showHelm;
      this.render();
    });

    // Preview Attack Swing Motion
    this.modalEl.querySelector('#btn-preview-attack')?.addEventListener('click', () => {
      player.attackAnimTimer = 0.28;
    });

    // Randomize & Reset Appearance
    this.modalEl.querySelector('#btn-custom-random')?.addEventListener('click', () => {
      const g = player.appearance?.gender || 'male';
      player.appearance = randomizeCharacterAppearance(g);
      this.render();
    });

    this.modalEl.querySelector('#btn-custom-reset')?.addEventListener('click', () => {
      player.appearance = getDefaultAppearanceForRole(player.role);
      this.render();
    });

    // Hair Style & Facial Feature selects
    this.modalEl.querySelector('#select-hair-style')?.addEventListener('change', (e) => {
      if (!player.appearance) player.appearance = getDefaultAppearanceForRole(player.role);
      player.appearance.hairStyle = (e.target as HTMLSelectElement).value as HairStyleId;
      player.appearance.showHelm = false; // Auto-reveal hair when changing hair style!
      this.render();
    });

    this.modalEl.querySelector('#select-facial-feature')?.addEventListener('change', (e) => {
      if (!player.appearance) player.appearance = getDefaultAppearanceForRole(player.role);
      player.appearance.facialFeature = (e.target as HTMLSelectElement).value as FacialFeatureId;
      this.render();
    });

    // Body Scale buttons
    this.modalEl.querySelectorAll('[data-body-scale]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!player.appearance) player.appearance = getDefaultAppearanceForRole(player.role);
        player.appearance.bodyScale = parseFloat(btn.getAttribute('data-body-scale') || '1.0');
        this.render();
      });
    });

    // Color Swatch Pickers (Skin, Hair, Primary Armor, Secondary Armor, Cape)
    const bindColorAttr = (
      attr: string,
      key: 'skinColor' | 'hairColor' | 'primaryArmorColor' | 'secondaryArmorColor' | 'capeColor'
    ) => {
      this.modalEl?.querySelectorAll(`[${attr}]`).forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!player.appearance) player.appearance = getDefaultAppearanceForRole(player.role);
          const hex = btn.getAttribute(attr);
          if (hex) {
            player.appearance[key] = hex;
            this.render();
          }
        });
      });
    };

    bindColorAttr('data-skin-color', 'skinColor');
    bindColorAttr('data-hair-color', 'hairColor');
    bindColorAttr('data-primary-color', 'primaryArmorColor');
    bindColorAttr('data-secondary-color', 'secondaryArmorColor');
    bindColorAttr('data-cape-color', 'capeColor');

    // Race selection
    this.modalEl.querySelectorAll('[data-select-race]').forEach((el) => {
      el.addEventListener('click', () => {
        const rKey = el.getAttribute('data-select-race') as RaceId;
        const rDef = TERRA_ANIMA_RACES[rKey];
        if (!rDef) return;
        if (!rDef.allowedRoles.includes(player.role)) {
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `⚠️ [다옥 종족 제한] [${rDef.nameKr}] 종족은 [${player.job}] 직업을 선택할 수 없습니다!`,
            category: 'interrupted',
          });
          return;
        }
        player.race = rKey;
        const bonus = player.allocatedBonusStats || { str: 10, con: 10, dex: 0, qui: 0, int: 0, pie: 10, cha: 0 };
        player.baseStats = {
          str: rDef.baseStats.str + bonus.str,
          con: rDef.baseStats.con + bonus.con,
          dex: rDef.baseStats.dex + bonus.dex,
          qui: rDef.baseStats.qui + bonus.qui,
          int: rDef.baseStats.int + bonus.int,
          pie: rDef.baseStats.pie + bonus.pie,
          cha: rDef.baseStats.cha + bonus.cha,
        };
        recalculateEntityStats(player, true);
        this.render();
      });
    });

    // Bonus stat +/- buttons
    const applyBonusChange = (newBonus: StatBlock) => {
      const rDef = TERRA_ANIMA_RACES[player.race || 'solarian'];
      player.allocatedBonusStats = newBonus;
      player.baseStats = {
        str: rDef.baseStats.str + newBonus.str,
        con: rDef.baseStats.con + newBonus.con,
        dex: rDef.baseStats.dex + newBonus.dex,
        qui: rDef.baseStats.qui + newBonus.qui,
        int: rDef.baseStats.int + newBonus.int,
        pie: rDef.baseStats.pie + newBonus.pie,
        cha: rDef.baseStats.cha + newBonus.cha,
      };
      recalculateEntityStats(player, true);
      this.render();
    };

    this.modalEl.querySelectorAll('[data-bonus-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const k = btn.getAttribute('data-bonus-plus') as keyof StatBlock;
        const cur = { ...(player.allocatedBonusStats || { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 }) };
        cur[k] = (cur[k] || 0) + 1;
        if (getTotalBonusPointsUsed(cur) <= 30) {
          applyBonusChange(cur);
        }
      });
    });

    this.modalEl.querySelectorAll('[data-bonus-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const k = btn.getAttribute('data-bonus-minus') as keyof StatBlock;
        const cur = { ...(player.allocatedBonusStats || { str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 }) };
        if (cur[k] > 0) {
          cur[k] -= 1;
          applyBonusChange(cur);
        }
      });
    });

    this.modalEl.querySelector('#btn-reset-bonus')?.addEventListener('click', () => {
      applyBonusChange({ str: 0, con: 0, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 });
    });

    this.modalEl.querySelector('#btn-preset-10-10-10')?.addEventListener('click', () => {
      if (player.role === 'caster_dps') applyBonusChange({ str: 0, con: 10, dex: 0, qui: 10, int: 10, pie: 0, cha: 0 });
      else if (player.role === 'ranged_dps') applyBonusChange({ str: 0, con: 10, dex: 10, qui: 10, int: 0, pie: 0, cha: 0 });
      else if (player.role === 'buffer') applyBonusChange({ str: 0, con: 10, dex: 0, qui: 10, int: 0, pie: 0, cha: 10 });
      else applyBonusChange({ str: 10, con: 10, dex: 0, qui: 0, int: 0, pie: 10, cha: 0 });
    });

    this.modalEl.querySelector('#btn-preset-15-10')?.addEventListener('click', () => {
      if (player.role === 'caster_dps') applyBonusChange({ str: 0, con: 0, dex: 0, qui: 10, int: 15, pie: 0, cha: 0 });
      else if (player.role === 'ranged_dps') applyBonusChange({ str: 0, con: 0, dex: 15, qui: 10, int: 0, pie: 0, cha: 0 });
      else if (player.role === 'buffer') applyBonusChange({ str: 0, con: 0, dex: 0, qui: 10, int: 0, pie: 0, cha: 15 });
      else if (player.role === 'healer') applyBonusChange({ str: 0, con: 10, dex: 0, qui: 0, int: 0, pie: 15, cha: 0 });
      else applyBonusChange({ str: 15, con: 10, dex: 0, qui: 0, int: 0, pie: 0, cha: 0 });
    });

    // Test HP ratio buttons (to verify HP-proportional Move Speed)
    this.modalEl.querySelectorAll('[data-test-hp]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const ratio = parseFloat(btn.getAttribute('data-test-hp') || '1.0');
        player.resources.hp = Math.max(1, Math.round(player.resources.maxHp * ratio));
        this.render();
      });
    });

    // Character Level Up & Spec Skill Training (관리자 전용 디버그 버튼)
    this.modalEl.querySelector('#btn-char-lv-up')?.addEventListener('click', () => {
      if (!SkillEngine.isEntityAdmin(player)) return;
      player.level = Math.min(50, (player.level || 15) + 5);
      recalculateEntityStats(player, false);
      this.render();
    });

    this.modalEl.querySelector('#btn-char-lv-50')?.addEventListener('click', () => {
      if (!SkillEngine.isEntityAdmin(player)) return;
      player.level = 50;
      recalculateEntityStats(player, false);
      this.render();
    });

    this.modalEl.querySelectorAll('[data-train-skill]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const s = parseInt(btn.getAttribute('data-train-skill') || '1', 10);
        if (!player.specSkillLevels) player.specSkillLevels = {};
        if (!player.selectedCastLevels) player.selectedCastLevels = {};
        const cur = player.specSkillLevels[s] || 1;
        const cost = cur + 1;
        if (cur < 50 && (player.specPointsAvailable || 0) >= cost) {
          player.specSkillLevels[s] = cur + 1;
          player.selectedCastLevels[s] = cur + 1;
          recalculateEntityStats(player, false);
          this.render();
        }
      });
    });

    this.modalEl.querySelectorAll('[data-castlv-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const s = parseInt(btn.getAttribute('data-castlv-minus') || '1', 10);
        if (!player.selectedCastLevels) return;
        player.selectedCastLevels[s] = Math.max(1, (player.selectedCastLevels[s] || 1) - 1);
        this.render();
      });
    });

    this.modalEl.querySelectorAll('[data-castlv-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const s = parseInt(btn.getAttribute('data-castlv-plus') || '1', 10);
        if (!player.selectedCastLevels || !player.specSkillLevels) return;
        const maxLv = player.specSkillLevels[s] || 1;
        player.selectedCastLevels[s] = Math.min(maxLv, (player.selectedCastLevels[s] || 1) + 1);
        this.render();
      });
    });

    // Self-skill training buttons
    this.modalEl.querySelectorAll('[data-practice-self]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-practice-self') as keyof SelfSkillBlock;
        const res = practiceSelfSkill(player, key, 2.5);
        if (res.statGained) {
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `🎉 [수련 스탯 상승!] [${player.name}] ${key} 수련 중 ${res.statGained}`,
            category: 'crafting',
          });
        }
        this.render();
      });
    });

    // Multi-skill Crafting & Enchanter buttons (Also automatically adds crafted item into Character's 20-Slot Inventory!)
    const pushCraftedToInv = (crafted: CraftedEquipment) => {
      addItemToInventory(player, {
        id: crafted.id,
        name: crafted.name,
        icon: crafted.slot === 'offhand' ? '🛡️' : '⚔️',
        category: crafted.slot,
        rarity: crafted.quality >= 100 ? 'legendary' : crafted.quality >= 99 ? 'masterwork' : 'fine',
        quantity: 1,
        maxStack: 1,
        weightKg: crafted.weightKg,
        valueGold: Currency.toCopper({ gold: Math.round(crafted.quality / 2) }),
        description: `3부품 제작 장비 (${crafted.components.primary.name} + ${crafted.components.secondary1.name} + ${crafted.components.secondary2.name})`,
        craftedEquip: crafted,
      });
    };

    this.modalEl.querySelectorAll('[data-craft-normal]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const recId = btn.getAttribute('data-craft-normal');
        const rec = MULTI_SKILL_RECIPES.find((r) => r.id === recId);
        if (rec) {
          this.latestCraftedItem = craftMultiSkillEquipment(player, rec, false);
          pushCraftedToInv(this.latestCraftedItem);
          this.render();
        }
      });
    });

    this.modalEl.querySelectorAll('[data-craft-master]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const recId = btn.getAttribute('data-craft-master');
        const rec = MULTI_SKILL_RECIPES.find((r) => r.id === recId);
        if (rec) {
          this.latestCraftedItem = craftMultiSkillEquipment(player, rec, true);
          pushCraftedToInv(this.latestCraftedItem);
          this.render();
        }
      });
    });

    // ========================================================================
    // 🎒 20-SLOT INVENTORY, HORSE TICKET BUY/HAND-OVER & STEALING (도둑질) EVENTS
    // ========================================================================
    const allEntities = this.registry.getAll();
    const partyList = this.registry.getParty();
    const npcList = allEntities.filter((e) => e.category === 'npc');
    const leader = player;
    const companions = partyList.filter((m) => m.id !== leader.id);
    const allTargets = [...npcList, ...companions];
    const activeTarget =
      allTargets.find((t) => t.id === this.selectedInvNpcId) || npcList[0] || companions[0] || null;

    // Fame Gain Buttons in Stat Sheet (Quest, Elite, Boss rewards)
    this.modalEl.querySelectorAll('[data-fame-gain]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const raw = (btn.getAttribute('data-fame-gain') || '100,퀘스트 보상').split(',');
        const delta = parseInt(raw[0], 10) || 100;
        const reason = raw[1] || '퀘스트 및 토벌 보상';
        adjustEntityFame(player, delta, reason);
        this.render();
      });
    });

    this.modalEl.querySelectorAll('[data-inv-select-npc]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.selectedInvNpcId = btn.getAttribute('data-inv-select-npc') || 'npc_stable_jackson';
        this.render();
      });
    });

    // Stable-to-Stable Route Controls in World Map Tab
    const selFrom = this.modalEl.querySelector('#sel-stable-from') as HTMLSelectElement | null;
    const selTo = this.modalEl.querySelector('#sel-stable-to') as HTMLSelectElement | null;
    selFrom?.addEventListener('change', () => {
      this.selectedFromStableId = selFrom.value;
    });
    selTo?.addEventListener('change', () => {
      this.selectedToStableId = selTo.value;
    });

    this.modalEl.querySelector('#btn-start-stable-route')?.addEventListener('click', () => {
      if (this.stableRouteSystem) {
        const started = this.stableRouteSystem.startRoute(this.selectedFromStableId, this.selectedToStableId, false);
        if (started) {
          this.isOpen = false;
          if (this.modalEl) this.modalEl.style.display = 'none';
        }
      }
    });

    this.modalEl.querySelectorAll('[data-quick-stable-route]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const pair = (btn.getAttribute('data-quick-stable-route') || '').split('->');
        if (pair.length === 2 && this.stableRouteSystem) {
          this.selectedFromStableId = pair[0];
          this.selectedToStableId = pair[1];
          const started = this.stableRouteSystem.startRoute(pair[0], pair[1], false);
          if (started) {
            this.isOpen = false;
            if (this.modalEl) this.modalEl.style.display = 'none';
          }
        }
      });
    });

    this.modalEl.querySelectorAll('[data-stable-ride-to]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const destId = btn.getAttribute('data-stable-ride-to') || 'stable_z1_oakhaven';
        if (this.stableRouteSystem) {
          const nearest = this.stableRouteSystem.getNearestStableInCurrentZone(player.x, player.y);
          const fromId = nearest.id === destId ? 'stable_z1_crossroads' : nearest.id;
          const started = this.stableRouteSystem.startRoute(fromId, destId, false);
          if (started) {
            this.isOpen = false;
            if (this.modalEl) this.modalEl.style.display = 'none';
          }
        }
      });
    });

    this.modalEl.querySelectorAll('[data-horse-coat]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const hex = btn.getAttribute('data-horse-coat') || '#f8fafc';
        leader.horseColor = hex;
        this.render();
      });
    });

    this.modalEl.querySelector('#btn-sort-char-inv')?.addEventListener('click', () => {
      sortInventory20(leader);
      this.render();
    });

    // 0-0) Party Leader Loot Filter Setting
    this.modalEl.querySelector('#btn-apply-loot-filter')?.addEventListener('click', () => {
      const sel = this.modalEl?.querySelector('#select-loot-rarity') as HTMLSelectElement | null;
      if (sel) {
        leader.lootFilterRarity = sel.value as any;
        const rarityNames: Record<string, string> = {
          common: '일반(회색)',
          magic: '마법(파란색)',
          rare: '희귀(노란색)',
          heroic: '영웅(보라색)',
          legendary: '전설(황금색)',
        };
        const label = rarityNames[sel.value] || sel.value;
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `👑 [루팅 필터 설정 완료] 이제 [${label}] 등급 이상의 전리품만 파티 리더 인벤토리에 보관되며, 그 미만의 전리품은 파티원에게 자동 분배됩니다 (1순위 직업적합, 2순위 어그로).`,
          category: 'system',
        });
        alert(`루팅 필터가 [${label}] 이상으로 설정되었습니다!\n\n• ${label} 이상: 파티 리더 인벤토리에 수납\n• ${label} 미만: 직업 1순위 / 어그로 2순위 파티원 자동분배`);
        this.render();
      }
    });

    // 0-1) Inventory Slot Expansion (+5 slots with 50 diamonds/gems)
    const handleExpandInv = () => {
      const curSlots = leader.unlockedSlots || INVENTORY_BASE_UNLOCKED;
      if (curSlots >= INVENTORY_CAPACITY) {
        alert('인벤토리가 이미 최대 50칸까지 확장되었습니다.');
        return;
      }

      // Check Diamonds from User Session or leader.diamonds
      let userSession: any = null;
      try {
        const raw = localStorage.getItem('terra_anima_user_session');
        if (raw) userSession = JSON.parse(raw);
      } catch { /* ignore */ }

      const userGems = userSession?.gems ?? (leader.diamonds || 0);
      if (userGems < INVENTORY_UNLOCK_DIAMOND_COST) {
        alert(`다이아가 부족합니다!\n(필요: 💎${INVENTORY_UNLOCK_DIAMOND_COST} 다이아 / 보유: 💎${userGems} 다이아)`);
        return;
      }

      const nextSlots = Math.min(INVENTORY_CAPACITY, curSlots + INVENTORY_UNLOCK_STEP);
      if (!confirm(`💎 다이아 ${INVENTORY_UNLOCK_DIAMOND_COST}개를 사용하여 인벤토리를 +${INVENTORY_UNLOCK_STEP}칸 확장하시겠습니까?\n(${curSlots}칸 ➔ ${nextSlots}칸)`)) {
        return;
      }

      // Deduct gems
      if (userSession && typeof userSession.gems === 'number') {
        userSession.gems -= INVENTORY_UNLOCK_DIAMOND_COST;
        localStorage.setItem('terra_anima_user_session', JSON.stringify(userSession));
      }
      if (leader.diamonds !== undefined) {
        leader.diamonds = Math.max(0, leader.diamonds - INVENTORY_UNLOCK_DIAMOND_COST);
      }

      // Increase slots
      leader.unlockedSlots = nextSlots;

      // Update in AccountSaveSystem if current slot is stored
      try {
        const allAccs = AccountSaveSystem.loadAllAccounts();
        for (const k of Object.keys(allAccs)) {
          const s = allAccs[k]?.slots?.find((slot: any) => slot.name === leader.name || slot.id === leader.id);
          if (s) {
            s.unlockedSlots = nextSlots;
            AccountSaveSystem.saveAllAccounts(allAccs);
            if (ApiClient.getToken() && s.id) {
              ApiClient.updateCharacter(s.id, { unlockedSlots: nextSlots }).catch(() => {});
            }
            break;
          }
        }
      } catch { /* ignore */ }

      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💎 [인벤토리 확장] 다이아 ${INVENTORY_UNLOCK_DIAMOND_COST}개를 사용하여 가방이 ${nextSlots}칸으로 확장되었습니다!`,
        category: 'system',
      });

      this.render();
    };

    this.modalEl.querySelector('#btn-expand-inv-5')?.addEventListener('click', handleExpandInv);
    this.modalEl.querySelectorAll('[data-locked-slot]').forEach((btn) => {
      btn.addEventListener('click', handleExpandInv);
    });

    // 0-2) Click currency badge to choose amount and pick up money onto cursor for trading
    this.modalEl.querySelectorAll('[data-money-click]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const tier = btn.getAttribute('data-money-click') as any;
        if (tier) {
          moneySelectorModalInstance.open(leader, tier);
        }
      });
    });

    // 1) Party Leader hands a Horse Route Ticket (승마표) back to the Horse Merchant -> Whole 6-person party mounts horses and departs!
    this.modalEl.querySelectorAll('[data-hand-ticket-slot]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(btn.getAttribute('data-hand-ticket-slot') || '0', 10);
        initEntityInventory20(leader);
        const ticket = leader.inventory![idx];
        if (!ticket || !this.stableRouteSystem) return;

        const nearest = this.stableRouteSystem.getNearestStableInCurrentZone(leader.x, leader.y);
        let targetStableId = ticket.routeTargetStableId || 'stable_z1_oakhaven';
        if (!ticket.routeTargetStableId) {
          if (ticket.name.includes('솔라리움')) targetStableId = 'stable_z1_solarium';
          else if (ticket.name.includes('십자로')) targetStableId = 'stable_z1_crossroads';
          else if (ticket.name.includes('크롬레크') || ticket.name.includes('그란델')) targetStableId = 'stable_z2_cromlech';
          else if (ticket.name.includes('실바나')) targetStableId = 'stable_z3_sylvana';
          else if (ticket.name.includes('노르드가드') || ticket.name.includes('보레아스')) targetStableId = 'stable_z4_nordgard';
          else if (ticket.name.includes('네뷸라')) targetStableId = 'stable_z5_nebula';
          else if (ticket.name.includes('이그니스')) targetStableId = 'stable_z6_ignis';
          else if (ticket.name.includes('루미나')) targetStableId = 'stable_z7_lumina';
        }
        const fromId = nearest.id === targetStableId ? 'stable_z1_crossroads' : nearest.id;

        // Consume 1 ticket and start party horse route (alreadyPaidByTicket = true)
        const started = this.stableRouteSystem.startRoute(fromId, targetStableId, true);
        if (started) {
          ticket.quantity -= 1;
          if (ticket.quantity <= 0) leader.inventory![idx] = null;
          this.isOpen = false;
          if (this.modalEl) this.modalEl.style.display = 'none';
        }
      });
    });

    this.modalEl.querySelectorAll('[data-char-slot-use]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(btn.getAttribute('data-char-slot-use') || '0', 10);
        initEntityInventory20(leader);
        const item = leader.inventory![idx];
        if (!item) return;

        if (item.category === 'consumable') {
          leader.resources.hp = Math.min(leader.resources.maxHp, leader.resources.hp + 120);
          leader.resources.mp = Math.min(leader.resources.maxMp, leader.resources.mp + 100);
          leader.resources.sp = Math.min(leader.resources.maxSp, leader.resources.sp + 100);
          item.quantity -= 1;
          if (item.quantity <= 0) leader.inventory![idx] = null;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `🧪 [${leader.name}] 이 [${item.name}] 을(를) 사용하여 HP/MP/SP를 회복했습니다!`,
            category: 'heal',
          });
        } else if (item.craftedEquip) {
          if (!leader.equipment) leader.equipment = {};
          leader.equipment[item.craftedEquip.slot] = item.craftedEquip;
          recalculateEntityStats(leader, false);
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `🛡️ [${leader.name}] 이 인벤토리에서 [${item.name}] 장비를 착용했습니다!`,
            category: 'system',
          });
        }
        this.render();
      });
    });

    this.modalEl.querySelector('#btn-run-enchanter')?.addEventListener('click', () => {
      if (!this.latestCraftedItem) return;

      // 1. 이미 인챈트된 장비인지 검증
      if (this.latestCraftedItem.enchants && this.latestCraftedItem.enchants.length > 0) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [인챈트 불가] 이미 마법부여된 장비는 마력이 고착되어 재인챈트할 수 없습니다!`,
          category: 'system',
        });
        return;
      }

      // 2. 인챈트 비용 확인 및 차감 (100골드)
      const enchantCost = Currency.toCopper({ gold: 100 });
      const currentGold = player.gold ?? 0;
      if (currentGold < enchantCost) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [인챈트 비용 부족] 마법부여 비용 100골드(${Currency.format(enchantCost)})가 부족합니다! (소지금: ${Currency.format(currentGold)})`,
          category: 'system',
        });
        return;
      }

      player.gold = currentGold - enchantCost;

      const skillSet = CLASS_SKILL_DEFINITIONS[player.role] || CLASS_SKILL_DEFINITIONS.tank;
      const nameMap: Record<number, string> = {};
      for (const k in skillSet) nameMap[Number(k)] = skillSet[Number(k)].nameKr;
      enchantEquipmentByNpc(this.latestCraftedItem, player, nameMap);

      const summary = this.latestCraftedItem.enchants.map((e) => `${e.targetName} +${e.value}`).join(', ');
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🔮 [인챈터 NPC] 100골드를 소모하여 [${this.latestCraftedItem.name}] 에 마법부여 완료! ➔ [ ${summary} ] (재인챈트 불가)`,
        category: 'crafting',
      });
      this.render();
    });

    this.modalEl.querySelector('#btn-equip-crafted')?.addEventListener('click', () => {
      if (!this.latestCraftedItem) return;
      if (!player.equipment) player.equipment = {};
      player.equipment[this.latestCraftedItem.slot] = this.latestCraftedItem;
      recalculateEntityStats(player, false);

      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🛡️ [장비 장착] [${player.name}] 이 [${this.latestCraftedItem.name}] 을(를) 장착했습니다! (스킬 보너스 합산 최대 +10 캡 적용)`,
        category: 'system',
      });
      this.render();
    });

    // Zone Teleport buttons
    this.modalEl.querySelectorAll('[data-teleport-zone]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const zId = parseInt(btn.getAttribute('data-teleport-zone') || '1', 10);
        if (this.onTeleportZone) {
          this.currentZoneId = zId;
          this.onTeleportZone(zId);
          // Close modal so the player immediately sees the new Zone terrain & entry banner!
          this.isOpen = false;
          if (this.modalEl) this.modalEl.style.display = 'none';
        }
      });
    });

    // Quick Tile Property Test Position Teleport buttons
    this.modalEl.querySelectorAll('[data-test-tile-pos]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const raw = (btn.getAttribute('data-test-tile-pos') || '80,80').split(',');
        const tx = parseFloat(raw[0]) || 80;
        const ty = parseFloat(raw[1]) || 80;
        if (this.onTeleportZone && this.currentZoneId !== 1) {
          this.onTeleportZone(1);
        }
        player.x = tx;
        player.y = ty;
        this.isOpen = false;
        if (this.modalEl) this.modalEl.style.display = 'none';
      });
    });
  }
}
